// THE HAAL PROJECT — IRL LENS DATA
// Ward-specific encounter data for all 9 cities

// ─── INTERFACES ──────────────────────────────────────────────────────────────

export interface NameOfAllah {
  arabic: string;
  transliteration: string;
  meaning: string;
}

export interface SunnatProcess {
  processName: string;
  namesOfAllah: NameOfAllah[];
  description: string;
  stemDepth?: string;
  psychDepth?: string;
}

export interface QuranAyat {
  ref: string;
  arabic: string;
  translation: string;
}

export interface IRLExample {
  id: string;
  name: string;
  arabicName: string;
  category: string;
  wardSpecific: string[];
  zahir: {
    sensory: string;
    sight: string;
    smell: string;
    texture: string;
    sound: string;
    huduthDhati: string;
    huduthZamani: string;
  };
  sunnatProcesses: SunnatProcess[];
  haqq: {
    zahirStatement: string;
    batinStatement: string;
    ayat: QuranAyat[];
  };
  amanah: {
    statement: string;
    namesActive: NameOfAllah[];
    reshatReference: string;
  };
  resourceCategories: string[];
}

export interface WardIRLData {
  wardId: string;
  wardName: string;
  cityId: string;
  catalogCount: number;
  examples: IRLExample[];
}

// ─── BASE EXAMPLES (shared across all wards) ─────────────────────────────────

export const BASE_EXAMPLES: IRLExample[] = [
  {
    id: 'fruit-tree',
    name: 'Fruit Tree',
    arabicName: 'شَجَرَةُ الثَّمَر',
    category: 'plant',
    wardSpecific: [],
    zahir: {
      sensory: 'A fruit tree in the neighborhood — roots in the earth, branches reaching upward, fruit hanging ready.',
      sight: 'Leaves, branches, fruit, roots visible at the base.',
      smell: 'The sweetness of ripening fruit, the earthiness of soil.',
      texture: 'Rough bark, smooth fruit skin, soft leaf surface.',
      sound: 'Wind through leaves, birds in branches, the occasional fruit falling.',
      huduthDhati: 'A fruit tree — its Huduth Dhati is the convergence of seed, soil, water, and sunlight into a living system that produces provision.',
      huduthZamani: 'Seed, then germination, then sapling, then mature tree, then fruit. Each stage a Huduth Zamani — a temporal event that reshapes the Huduth Dhati.'
    },
    sunnatProcesses: [
      {
        processName: 'Photosynthesis',
        namesOfAllah: [
          { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — sunlight converted to provision' },
          { arabic: 'الْحَكِيم', transliteration: 'Al-Hakim', meaning: 'The All-Wise — chlorophyll captures 430nm violet and 680nm red while reflecting green' }
        ],
        description: 'The leaf captures specific wavelengths of sunlight and converts carbon dioxide and water into glucose — the primary energy currency of life. Every photon that strikes a chlorophyll molecule is Rizq arriving at its destination.',
        stemDepth: 'Chlorophyll a and b absorb light at 430nm (violet) and 680nm (red), reflecting green wavelengths — this selectivity is Al-Hakeem in molecular design. The efficiency of this process (approximately 11% of total solar energy) represents a precise divine calibration.',
        psychDepth: 'The human eye perceives the green of leaves as restful and restorative — this is not coincidence. Al-Basir (The All-Seeing) gave us sight calibrated to the dominant wavelength of the living world.'
      },
      {
        processName: 'Transpiration',
        namesOfAllah: [
          { arabic: 'اللَّطِيف', transliteration: 'Al-Latif', meaning: 'The Subtle — invisible water movement through the tree' },
          { arabic: 'الرَّحِيم', transliteration: 'Ar-Rahim', meaning: 'The Especially Merciful — the tree cooling its environment' }
        ],
        description: 'The tree releases water vapor through its leaves — cooling the air, contributing to the water cycle, and creating the conditions for its own provision. Al-Latif\'s subtle generosity operating at the molecular level.',
      },
      {
        processName: 'Nutrient Cycling',
        namesOfAllah: [
          { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'The Giver of Life — nutrients cycling through living systems' },
          { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — provision cycling through the ecosystem' }
        ],
        description: 'Fallen leaves decompose, returning nutrients to the soil that fed the tree — a cycle of provision that mirrors the cycle of Rizq. Al-Muhyi operating in the invisible world of soil microorganisms.',
      },
      {
        processName: 'Fruit Production',
        namesOfAllah: [
          { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — fruit as provision for all who pass' },
          { arabic: 'الْوَهَّاب', transliteration: 'Al-Wahhab', meaning: 'The Bestower — fruit given freely, without condition' }
        ],
        description: 'The tree produces fruit beyond its own needs — provision for birds, insects, humans, and the soil. Al-Wahhab\'s generosity expressed through the biology of fruiting.',
      },
      {
        processName: 'Root Anchoring',
        namesOfAllah: [
          { arabic: 'الْمَتِين', transliteration: 'Al-Mateen', meaning: 'The Firm — roots as divine firmness in the earth' },
          { arabic: 'الْقَوِيّ', transliteration: 'Al-Qawi', meaning: 'The Strong — strength through rootedness' }
        ],
        description: 'The root system anchors the tree against wind, drought, and disturbance — a Sunnat of Al-Mateen expressed in the biology of rootedness.',
      }
    ],
    haqq: {
      zahirStatement: 'A fruit tree in the neighborhood.',
      batinStatement: 'Ar-Razzaq\'s provision made visible — and a question about who has access to this provision. The fruit tree is both a sign and a responsibility.',
      ayat: [
        { ref: '6:99', arabic: 'وَهُوَ الَّذِي أَنزَلَ مِنَ السَّمَاءِ مَاءً فَأَخْرَجْنَا بِهِ نَبَاتَ كُلِّ شَيْءٍ', translation: 'And it is He who sends down rain from the sky, and We produce thereby the growth of all things.' },
        { ref: '16:11', arabic: 'يُنبِتُ لَكُم بِهِ الزَّرْعَ وَالزَّيْتُونَ وَالنَّخِيلَ وَالْأَعْنَابَ وَمِن كُلِّ الثَّمَرَاتِ', translation: 'He causes to grow for you thereby the crops, olives, palm trees, grapevines, and from all the fruits.' }
      ]
    },
    amanah: {
      statement: 'Plant fruit trees in the neighborhood. Ensure their provision reaches all who need it. Protect them from removal.',
      namesActive: [
        { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'Provision being actively maintained' },
        { arabic: 'الْوَهَّاب', transliteration: 'Al-Wahhab', meaning: 'Generosity as Amanah' }
      ],
      reshatReference: 'The Caretaker who plants a fruit tree and shares its provision wears a thread of Taqwah.'
    },
    resourceCategories: ['Food', 'Community', 'Health']
  },
  {
    id: 'fire-hydrant',
    name: 'Fire Hydrant',
    arabicName: 'حَنَفِيَّةُ الْإِطْفَاء',
    category: 'structure',
    wardSpecific: [],
    zahir: {
      sensory: 'A fire hydrant on the sidewalk — red or yellow, connected to the water main beneath the street.',
      sight: 'The hydrant itself, the street, the infrastructure it connects to.',
      smell: 'Metal, concrete, the smell of a working city.',
      texture: 'Cold metal, rough concrete.',
      sound: 'The city around it — traffic, voices, the hum of infrastructure.',
      huduthDhati: 'A fire hydrant — its Huduth Dhati is the convergence of water pressure, infrastructure, and community protection into a single point of access.',
      huduthZamani: 'The water main was laid, then the hydrant was installed, then the neighborhood grew around it. Each stage a Huduth Zamani.'
    },
    sunnatProcesses: [
      {
        processName: 'Water Pressure',
        namesOfAllah: [
          { arabic: 'الْقَادِر', transliteration: 'Al-Qadir', meaning: 'The All-Powerful — pressure as potential power' },
          { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — water as provision for protection' }
        ],
        description: 'The water pressure in the main is potential energy — Al-Qadir\'s power stored in the infrastructure, ready to be released in service of protection.',
      },
      {
        processName: 'Infrastructure Equity',
        namesOfAllah: [
          { arabic: 'الْعَدْل', transliteration: 'Al-Adl', meaning: 'The Just — equal protection as divine justice' },
          { arabic: 'الْمُقْسِط', transliteration: 'Al-Muqsit', meaning: 'The Equitable — equitable infrastructure as Amanah' }
        ],
        description: 'The presence or absence of functioning fire hydrants is a measure of Al-Adl in infrastructure — communities with broken or missing hydrants are communities whose protection has been devalued.',
      }
    ],
    haqq: {
      zahirStatement: 'A fire hydrant on the sidewalk.',
      batinStatement: 'Al-Qadir\'s power stored in infrastructure — and a question about equity of protection. The hydrant is both a sign of provision and a measure of justice.',
      ayat: [
        { ref: '21:30', arabic: 'وَجَعَلْنَا مِنَ الْمَاءِ كُلَّ شَيْءٍ حَيٍّ', translation: 'And We made from water every living thing.' }
      ]
    },
    amanah: {
      statement: 'Ensure fire hydrants in the neighborhood are functioning. Advocate for equitable infrastructure maintenance.',
      namesActive: [
        { arabic: 'الْعَدْل', transliteration: 'Al-Adl', meaning: 'Equitable protection as Amanah' },
        { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'Water provision as divine right' }
      ],
      reshatReference: 'The Caretaker who advocates for functioning infrastructure wears a thread of Taqwah.'
    },
    resourceCategories: ['Community', 'Justice', 'Health']
  },
  {
    id: 'community-garden',
    name: 'Community Garden',
    arabicName: 'الْحَدِيقَةُ الْمُجْتَمَعِيَّة',
    category: 'community',
    wardSpecific: [],
    zahir: {
      sensory: 'A community garden — raised beds, growing vegetables, people tending their plots.',
      sight: 'Raised beds, growing plants, community members, tools.',
      smell: 'Fresh soil, growing plants, the smell of a living garden.',
      texture: 'Rich soil, rough wood of raised beds, smooth vegetable skins.',
      sound: 'Community voices, birds, the sounds of a living garden.',
      huduthDhati: 'A community garden — its Huduth Dhati is the convergence of soil, seed, water, sunlight, and community into a shared provision system.',
      huduthZamani: 'Vacant lot, then soil preparation, then planting, then growing, then harvest, then community. Each stage a Huduth Zamani.'
    },
    sunnatProcesses: [
      {
        processName: 'Soil Ecology',
        namesOfAllah: [
          { arabic: 'الْخَالِق', transliteration: 'Al-Khaliq', meaning: 'The Creator — soil life as invisible creation' },
          { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — soil fertility as provision' }
        ],
        description: 'The soil in a community garden is a living system — bacteria, fungi, invertebrates, and plant roots creating the conditions for provision. Al-Khaliq\'s creation operating in the invisible world beneath our feet.',
      },
      {
        processName: 'Collective Cultivation',
        namesOfAllah: [
          { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'The Gatherer — community gathering around shared provision' },
          { arabic: 'الْوَدُود', transliteration: 'Al-Wadud', meaning: 'The Loving — love expressed through shared cultivation' }
        ],
        description: 'The community garden gathers people around shared provision — Al-Jami manifest in the collective cultivation of food. The garden is both provision and community.',
      }
    ],
    haqq: {
      zahirStatement: 'A community garden in the neighborhood.',
      batinStatement: 'Al-Khaliq\'s creation made visible — and Al-Jami\'s gathering expressed through shared cultivation. The garden is both a sign and a responsibility.',
      ayat: [
        { ref: '2:22', arabic: 'الَّذِي جَعَلَ لَكُمُ الْأَرْضَ فِرَاشًا وَالسَّمَاءَ بِنَاءً وَأَنزَلَ مِنَ السَّمَاءِ مَاءً فَأَخْرَجَ بِهِ مِنَ الثَّمَرَاتِ رِزْقًا لَّكُمْ', translation: 'Who made for you the earth a bed and the sky a ceiling and sent down from the sky rain and brought forth thereby fruits as provision for you.' }
      ]
    },
    amanah: {
      statement: 'Support community gardens in the neighborhood. Ensure their provision reaches all who need it. Protect them from removal.',
      namesActive: [
        { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'Provision being actively maintained' },
        { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'Community gathering as Amanah' }
      ],
      reshatReference: 'The Caretaker who tends the community garden wears a thread of Taqwah.'
    },
    resourceCategories: ['Food', 'Community', 'Health']
  }
];

// ─── WARD IRL DATA ────────────────────────────────────────────────────────────

export const WARD_IRL_DATA: WardIRLData[] = [
  // ── CHICAGO — ENGLEWOOD ───────────────────────────────────────────────────
  {
    wardId: 'englewood',
    wardName: 'Englewood',
    cityId: 'chicago',
    catalogCount: 47,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'englewood-prairie-grass',
        name: 'Native Prairie Grass (Big Bluestem)',
        arabicName: 'عُشْبُ السَّهْل الأَصِيل',
        category: 'plant',
        wardSpecific: ['englewood'],
        zahir: {
          sensory: 'Big Bluestem prairie grass growing in a vacant lot in Englewood — 6 feet tall, roots going 12 feet deep, returning to the land that was prairie before the city.',
          sight: 'Tall blue-green grass, seed heads like turkey feet, the contrast with surrounding urban landscape.',
          smell: 'Dry grass, earth, the smell of the prairie returning.',
          texture: 'Rough grass stems, smooth seed heads.',
          sound: 'Wind through tall grass, insects, the sounds of a returning ecosystem.',
          huduthDhati: 'Big Bluestem — its Huduth Dhati is the convergence of deep roots, drought tolerance, and fire adaptation into a grass that shaped the Great Plains.',
          huduthZamani: 'Prairie, then settlement, then city, then abandonment, then prairie returning. Each stage a Huduth Zamani.'
        },
        sunnatProcesses: [
          {
            processName: 'Deep Root System',
            namesOfAllah: [
              { arabic: 'الْمَتِين', transliteration: 'Al-Mateen', meaning: 'The Firm — roots going 12 feet deep as divine firmness' },
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — deep roots accessing provision unavailable to shallow-rooted plants' }
            ],
            description: 'Big Bluestem roots go 12 feet deep — accessing water and nutrients unavailable to shallow-rooted plants, surviving drought that kills other species. Al-Mateen\'s firmness expressed in the biology of rootedness.',
          },
          {
            processName: 'Carbon Sequestration',
            namesOfAllah: [
              { arabic: 'الْحَافِظ', transliteration: 'Al-Hafiz', meaning: 'The Preserver — carbon stored in deep roots as preservation' }
            ],
            description: 'Prairie grasses store more carbon in their roots than forests store in their trunks — a Sunnat of Al-Hafiz operating in the invisible world beneath the surface.',
          }
        ],
        haqq: {
          zahirStatement: 'Big Bluestem prairie grass in a vacant lot in Englewood.',
          batinStatement: 'Al-Mateen\'s firmness expressed in deep roots — and a sign of what the land was before the city. The prairie returning to Englewood is both ecological restoration and a question about what restoration looks like for the community.',
          ayat: [
            { ref: '14:24', arabic: 'أَلَمْ تَرَ كَيْفَ ضَرَبَ اللَّهُ مَثَلًا كَلِمَةً طَيِّبَةً كَشَجَرَةٍ طَيِّبَةٍ أَصْلُهَا ثَابِتٌ وَفَرْعُهَا فِي السَّمَاءِ', translation: 'Do you not see how Allah presents an example: a good word like a good tree, whose root is firmly fixed and its branches are in the sky.' }
          ]
        },
        amanah: {
          statement: 'Support prairie restoration in Englewood. Connect ecological restoration to community development — the returning prairie as a sign of what is possible.',
          namesActive: [
            { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'Life returning to abandoned land' },
            { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'Provision through ecological restoration' }
          ],
          reshatReference: 'The Caretaker who plants native prairie grass in a vacant lot wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Health', 'Education']
      },
      {
        id: 'englewood-vacant-lot',
        name: 'Abandoned Lot / Urban Void',
        arabicName: 'الْفَرَاغُ الْحَضَرِيّ',
        category: 'structure',
        wardSpecific: ['englewood'],
        zahir: {
          sensory: 'An abandoned lot in Englewood — the absence of what was here, the presence of what is returning.',
          sight: 'Overgrown vegetation, broken concrete, the signs of absence.',
          smell: 'Dry earth, vegetation, the smell of a space between uses.',
          texture: 'Rough concrete, soft soil, the texture of transition.',
          sound: 'Wind, birds, the sounds of a space reclaiming itself.',
          huduthDhati: 'An urban void — its Huduth Dhati is the absence of the structure that was here and the presence of what is returning.',
          huduthZamani: 'Structure, then abandonment, then demolition, then void, then return. Each stage a Huduth Zamani.'
        },
        sunnatProcesses: [
          {
            processName: 'Ecological Succession in Urban Voids',
            namesOfAllah: [
              { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'The Giver of Life — life returning to abandoned spaces' },
              { arabic: 'الْمُبْدِئ', transliteration: 'Al-Mubdi', meaning: 'The Originator — new beginnings in abandoned spaces' }
            ],
            description: 'The abandoned lot is not empty — it is a space of ecological succession, where pioneer species are preparing the ground for what comes next. Al-Muhyi operating in the absence of human action.',
          }
        ],
        haqq: {
          zahirStatement: 'An abandoned lot in Englewood.',
          batinStatement: 'Al-Muhyi operating in the absence — and a question about what this space could become. The void is both a sign of disinvestment and an opportunity for restoration.',
          ayat: [
            { ref: '22:5', arabic: 'وَتَرَى الْأَرْضَ هَامِدَةً فَإِذَا أَنزَلْنَا عَلَيْهَا الْمَاءَ اهْتَزَّتْ وَرَبَتْ', translation: 'You see the earth barren, but when We send down rain upon it, it quivers and swells.' }
          ]
        },
        amanah: {
          statement: 'Transform abandoned lots in Englewood into community assets — gardens, parks, gathering spaces. The void as opportunity for restoration.',
          namesActive: [
            { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'Life returning to abandoned spaces' },
            { arabic: 'الْمُعِيد', transliteration: 'Al-Mu\'id', meaning: 'Restoration as Islah' }
          ],
          reshatReference: 'The Caretaker who transforms an abandoned lot into a community asset wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Justice', 'Health']
      }
    ]
  },
  // ── CHICAGO — SOUTH SHORE ─────────────────────────────────────────────────
  {
    wardId: 'south-shore',
    wardName: 'South Shore',
    cityId: 'chicago',
    catalogCount: 38,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'south-shore-lake',
        name: 'Lake Michigan (Great Lakes Watershed)',
        arabicName: 'بُحَيْرَةُ مِيشِيغَان',
        category: 'water',
        wardSpecific: ['south-shore'],
        zahir: {
          sensory: 'Lake Michigan at the South Shore — the largest body of fresh water in the world by surface area, visible from the neighborhood.',
          sight: 'The lake, the horizon, the water meeting the sky.',
          smell: 'Fresh water, the smell of the lake.',
          texture: 'Water, sand, the texture of a shoreline.',
          sound: 'Waves, birds, the sounds of a great lake.',
          huduthDhati: 'Lake Michigan — its Huduth Dhati is the convergence of glacial history, watershed ecology, and human use into a body of water that sustains millions.',
          huduthZamani: 'Glacial formation, then ecological succession, then human settlement, then industrial use, then restoration. Each stage a Huduth Zamani.'
        },
        sunnatProcesses: [
          {
            processName: 'Watershed Ecology',
            namesOfAllah: [
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — fresh water as provision for millions' },
              { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'The Giver of Life — water as the source of all life' }
            ],
            description: 'The Great Lakes watershed holds 21% of the world\'s surface fresh water — a Sunnat of Ar-Razzaq operating at a continental scale.',
          },
          {
            processName: 'Migratory Bird Corridor',
            namesOfAllah: [
              { arabic: 'الْهَادِي', transliteration: 'Al-Hadi', meaning: 'The Guide — birds guided across thousands of miles' },
              { arabic: 'الْعَلِيم', transliteration: 'Al-Alim', meaning: 'The All-Knowing — knowledge of migration routes encoded in biology' }
            ],
            description: 'The Lake Michigan shoreline is a major migratory bird corridor — millions of birds guided by Al-Hadi across thousands of miles, using the lake as a navigation landmark.',
          }
        ],
        haqq: {
          zahirStatement: 'Lake Michigan at the South Shore.',
          batinStatement: 'Ar-Razzaq\'s provision at a continental scale — and a question about who has access to this provision. The lake is both a blessing and a responsibility.',
          ayat: [
            { ref: '21:30', arabic: 'وَجَعَلْنَا مِنَ الْمَاءِ كُلَّ شَيْءٍ حَيٍّ', translation: 'And We made from water every living thing.' }
          ]
        },
        amanah: {
          statement: 'Protect Lake Michigan and the Great Lakes watershed. Ensure South Shore residents have access to the lake and its provision.',
          namesActive: [
            { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'Fresh water as provision' },
            { arabic: 'الْحَافِظ', transliteration: 'Al-Hafiz', meaning: 'Protecting the watershed as Amanah' }
          ],
          reshatReference: 'The Caretaker who protects the Great Lakes wears a thread of Taqwah.'
        },
        resourceCategories: ['Health', 'Community', 'Justice']
      },
      {
        id: 'south-shore-birds',
        name: 'Migratory Birds (Lakefront Corridor)',
        arabicName: 'الطُّيُورُ الْمُهَاجِرَة',
        category: 'animal',
        wardSpecific: ['south-shore'],
        zahir: {
          sensory: 'Migratory birds at the South Shore lakefront — warblers, shorebirds, raptors passing through on their way north or south.',
          sight: 'Birds in the trees, on the shore, in the air.',
          smell: 'The lake, the vegetation, the smell of migration season.',
          texture: 'Feathers, the texture of a living corridor.',
          sound: 'Bird calls, the sounds of migration.',
          huduthDhati: 'A migratory bird — its Huduth Dhati is the convergence of navigation, endurance, and provision into a creature that crosses continents.',
          huduthZamani: 'Breeding, then migration, then wintering, then return. Each stage a Huduth Zamani.'
        },
        sunnatProcesses: [
          {
            processName: 'Magnetic Navigation',
            namesOfAllah: [
              { arabic: 'الْهَادِي', transliteration: 'Al-Hadi', meaning: 'The Guide — birds guided by magnetic fields' },
              { arabic: 'الْعَلِيم', transliteration: 'Al-Alim', meaning: 'The All-Knowing — knowledge encoded in biology' }
            ],
            description: 'Migratory birds navigate using the Earth\'s magnetic field, star patterns, and landmarks — Al-Hadi\'s guidance encoded in their biology. The knowledge of the route is given without books or teachers.',
          }
        ],
        haqq: {
          zahirStatement: 'Migratory birds at the South Shore lakefront.',
          batinStatement: 'Al-Hadi\'s guidance expressed in the biology of migration — and a sign of the interconnectedness of creation. The birds that pass through South Shore connect it to the Arctic and the tropics.',
          ayat: [
            { ref: '67:19', arabic: 'أَوَلَمْ يَرَوْا إِلَى الطَّيْرِ فَوْقَهُمْ صَافَّاتٍ وَيَقْبِضْنَ ۚ مَا يُمْسِكُهُنَّ إِلَّا الرَّحْمَٰنُ', translation: 'Do they not look at the birds above them, spreading and contracting their wings? None holds them up except the Most Merciful.' }
          ]
        },
        amanah: {
          statement: 'Protect the lakefront corridor for migratory birds. Reduce light pollution and glass collisions. Connect South Shore residents to the migratory corridor.',
          namesActive: [
            { arabic: 'الْهَادِي', transliteration: 'Al-Hadi', meaning: 'Divine guidance in migration' },
            { arabic: 'الرَّحْمَن', transliteration: 'Ar-Rahman', meaning: 'The Most Merciful — holding the birds in flight' }
          ],
          reshatReference: 'The Caretaker who protects the migratory corridor wears a thread of Taqwah.'
        },
        resourceCategories: ['Health', 'Education', 'Community']
      }
    ]
  },
  // ── ATLANTA — WEST END ────────────────────────────────────────────────────
  {
    wardId: 'west-end',
    wardName: 'West End / Westview',
    cityId: 'atlanta',
    catalogCount: 52,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'west-end-beltline',
        name: 'BeltLine Trail (Urban Green Corridor)',
        arabicName: 'مَمَرُّ الْحَزَام الأَخْضَر',
        category: 'community',
        wardSpecific: ['west-end'],
        zahir: {
          sensory: 'The Atlanta BeltLine trail through West End — a former rail corridor transformed into a walking and cycling path connecting neighborhoods.',
          sight: 'The trail, trees, community members walking and cycling, murals.',
          smell: 'Trees, the smell of a green corridor in the city.',
          texture: 'Smooth pavement, rough tree bark, the texture of a living corridor.',
          sound: 'Community voices, birds, the sounds of a living trail.',
          huduthDhati: 'A green corridor — its Huduth Dhati is the convergence of former rail infrastructure, ecological restoration, and community connection into a living pathway.',
          huduthZamani: 'Rail corridor, then abandonment, then transformation, then community trail. Each stage a Huduth Zamani.'
        },
        sunnatProcesses: [
          {
            processName: 'Urban Ecological Corridor',
            namesOfAllah: [
              { arabic: 'الرَّحِيم', transliteration: 'Ar-Rahim', meaning: 'The Especially Merciful — green space as mercy for urban communities' },
              { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'The Giver of Life — life returning to former industrial corridor' }
            ],
            description: 'The BeltLine trail is a Sunnat of Al-Muhyi — life returning to a former industrial corridor, creating ecological and community connection.',
          },
          {
            processName: 'Community Walking Practice',
            namesOfAllah: [
              { arabic: 'الْبَصِير', transliteration: 'Al-Basir', meaning: 'The All-Seeing — walking as trained observation' },
              { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'The Gatherer — trail gathering community' }
            ],
            description: 'Walking the BeltLine is a practice of Al-Basir — trained observation of the neighborhood, seeing what is there and what is absent.',
          }
        ],
        haqq: {
          zahirStatement: 'The BeltLine trail through West End.',
          batinStatement: 'Al-Muhyi\'s life-giving expressed in the transformation of industrial infrastructure — and a question about who benefits from this transformation. The BeltLine is both a blessing and a question about equitable development.',
          ayat: [
            { ref: '25:63', arabic: 'وَعِبَادُ الرَّحْمَٰنِ الَّذِينَ يَمْشُونَ عَلَى الْأَرْضِ هَوْنًا', translation: 'And the servants of the Most Merciful are those who walk upon the earth easily.' }
          ]
        },
        amanah: {
          statement: 'Support equitable development along the BeltLine. Ensure West End residents benefit from the trail and are not displaced by it.',
          namesActive: [
            { arabic: 'الرَّحِيم', transliteration: 'Ar-Rahim', meaning: 'Green space as mercy' },
            { arabic: 'الْعَدْل', transliteration: 'Al-Adl', meaning: 'Equitable development as justice' }
          ],
          reshatReference: 'The Caretaker who walks the BeltLine with intention wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Health', 'Justice']
      },
      {
        id: 'west-end-live-oak',
        name: 'Southern Live Oak',
        arabicName: 'بَلُّوطُ الْجَنُوب',
        category: 'plant',
        wardSpecific: ['west-end'],
        zahir: {
          sensory: 'A Southern Live Oak in West End — massive, spreading, draped with Spanish moss, roots lifting the sidewalk.',
          sight: 'The massive canopy, the Spanish moss, the roots, the shade.',
          smell: 'The smell of a large tree, Spanish moss, the Piedmont forest.',
          texture: 'Rough bark, smooth leaves, the texture of a living canopy.',
          sound: 'Wind through the canopy, birds, the sounds of a living tree.',
          huduthDhati: 'A Southern Live Oak — its Huduth Dhati is the convergence of deep roots, massive canopy, and centuries of growth into a tree that shapes its environment.',
          huduthZamani: 'Acorn, then sapling, then young tree, then mature tree, then ancient tree. Each stage a Huduth Zamani measured in decades.'
        },
        sunnatProcesses: [
          {
            processName: 'Canopy Ecology',
            namesOfAllah: [
              { arabic: 'الرَّحِيم', transliteration: 'Ar-Rahim', meaning: 'The Especially Merciful — shade as mercy for the community' },
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — acorns as provision for wildlife' }
            ],
            description: 'The Live Oak canopy provides shade, habitat, and provision — a Sunnat of Ar-Rahim expressed in the biology of a massive tree.',
          }
        ],
        haqq: {
          zahirStatement: 'A Southern Live Oak in West End.',
          batinStatement: 'Ar-Rahim\'s mercy expressed in the shade of a massive tree — and a sign of the Piedmont forest that was here before the city. The Live Oak is both a blessing and a responsibility.',
          ayat: [
            { ref: '16:68', arabic: 'وَأَوْحَىٰ رَبُّكَ إِلَى النَّحْلِ أَنِ اتَّخِذِي مِنَ الْجِبَالِ بُيُوتًا وَمِنَ الشَّجَرِ', translation: 'And your Lord inspired to the bee, "Take for yourself among the mountains, houses, and among the trees."' }
          ]
        },
        amanah: {
          statement: 'Protect the Live Oaks in West End. Advocate for tree preservation in neighborhood development. Plant new Live Oaks.',
          namesActive: [
            { arabic: 'الرَّحِيم', transliteration: 'Ar-Rahim', meaning: 'Shade as mercy' },
            { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'Tree provision as Amanah' }
          ],
          reshatReference: 'The Caretaker who protects the Live Oak wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Health', 'Education']
      }
    ]
  },

  // ── CHICAGO — PILSEN ─────────────────────────────────────────────────────
  {
    wardId: 'pilsen',
    wardName: 'Pilsen',
    cityId: 'chicago',
    catalogCount: 38,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'pilsen-mural-garden',
        name: 'Mural Garden (18th Street Corridor)',
        arabicName: 'حَدِيقَةُ الْجِدَارِيَّات',
        category: 'community',
        wardSpecific: ['pilsen'],
        zahir: {
          sensory: 'A community garden adjacent to a mural wall on 18th Street — painted concrete, raised beds, the smell of soil and spray paint.',
          sight: 'Murals depicting cultural heritage, raised garden beds, community members tending plants.',
          smell: 'Soil, herbs, the faint smell of paint on a warm day.',
          texture: 'Rough concrete, soft soil, smooth painted surfaces.',
          sound: 'Community voices, birds, the sounds of a living neighborhood.',
          huduthDhati: 'A mural garden — its Huduth Dhati is the convergence of cultural expression, ecological cultivation, and community identity into a living space.',
          huduthZamani: 'Vacant lot, then mural, then garden, then community hub. Each stage a Huduth Zamani.'
        },
        sunnatProcesses: [
          {
            processName: 'Cultural Ecological Memory',
            namesOfAllah: [
              { arabic: 'الْحَفِيظ', transliteration: 'Al-Hafiz', meaning: 'The Preserver — cultural memory preserved in murals' },
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — food grown in community soil' }
            ],
            description: 'The mural garden preserves cultural memory while producing provision — Al-Hafiz and Ar-Razzaq operating together in a single community space.',
          }
        ],
        haqq: {
          zahirStatement: 'A mural garden on 18th Street in Pilsen.',
          batinStatement: 'Al-Hafiz\'s preservation expressed in cultural memory — and Ar-Razzaq\'s provision expressed in community food cultivation. The mural garden is both memory and sustenance.',
          ayat: [{ ref: '16:11', arabic: 'يُنبِتُ لَكُم بِهِ الزَّرْعَ وَالزَّيْتُونَ وَالنَّخِيلَ وَالْأَعْنَابَ', translation: 'He causes to grow for you with it the crops and the olives and the date palms and the grapes.' }]
        },
        amanah: {
          statement: 'Support community gardens in Pilsen. Protect cultural murals from development pressure. Connect food cultivation to cultural identity.',
          namesActive: [
            { arabic: 'الْحَفِيظ', transliteration: 'Al-Hafiz', meaning: 'Cultural preservation as Amanah' },
            { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'Food provision as Amanah' }
          ],
          reshatReference: 'The Caretaker who tends the mural garden wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Health', 'Education']
      }
    ]
  },

  // ── CHICAGO — WOODLAWN ────────────────────────────────────────────────────
  {
    wardId: 'woodlawn',
    wardName: 'Woodlawn',
    cityId: 'chicago',
    catalogCount: 35,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'woodlawn-vacant-lot-prairie',
        name: 'Vacant Lot Prairie (Urban Meadow)',
        arabicName: 'مَرْجُ الأَرَاضِي الشَّاغِرَة',
        category: 'plant',
        wardSpecific: ['woodlawn'],
        zahir: {
          sensory: 'A vacant lot in Woodlawn reclaimed by native prairie plants — goldenrod, milkweed, wild bergamot growing through cracked concrete.',
          sight: 'Native plants, pollinators, cracked concrete, the Chicago skyline in the distance.',
          smell: 'Wild bergamot, goldenrod, the smell of a living meadow in the city.',
          texture: 'Rough concrete, soft plant stems, the texture of a reclaimed space.',
          sound: 'Bees, birds, wind through tall grasses.',
          huduthDhati: 'A vacant lot prairie — its Huduth Dhati is the convergence of urban abandonment, ecological resilience, and native plant succession into a living meadow.',
          huduthZamani: 'Building, then demolition, then abandonment, then prairie succession. Each stage a Huduth Zamani.'
        },
        sunnatProcesses: [
          {
            processName: 'Ecological Succession',
            namesOfAllah: [
              { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'The Giver of Life — life returning to abandoned land' },
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — native plants providing for pollinators' }
            ],
            description: 'Ecological succession in a vacant lot is Al-Muhyi\'s life-giving expressed in the biology of native plant communities — life returning to abandoned urban land.',
          }
        ],
        haqq: {
          zahirStatement: 'A vacant lot prairie in Woodlawn.',
          batinStatement: 'Al-Muhyi\'s life-giving expressed in ecological resilience — and a question about what the land was before and what it could become. The vacant lot prairie is both a sign and a question.',
          ayat: [{ ref: '7:57', arabic: 'وَهُوَ الَّذِي يُرْسِلُ الرِّيَاحَ بُشْرًا بَيْنَ يَدَيْ رَحْمَتِهِ', translation: 'And He is the One Who sends the winds as glad tidings before His mercy.' }]
        },
        amanah: {
          statement: 'Support community land trusts in Woodlawn. Convert vacant lots to community gardens and native plant meadows. Advocate for equitable development.',
          namesActive: [
            { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'Life-giving as Amanah' },
            { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'Provision as Amanah' }
          ],
          reshatReference: 'The Caretaker who tends the vacant lot prairie wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Health', 'Justice']
      }
    ]
  },

  // ── CHICAGO — AUBURN GRESHAM ──────────────────────────────────────────────
  {
    wardId: 'auburn-gresham',
    wardName: 'Auburn Gresham',
    cityId: 'chicago',
    catalogCount: 33,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'auburn-gresham-community-garden',
        name: 'Auburn Gresham Community Garden',
        arabicName: 'حَدِيقَةُ الْمُجْتَمَع',
        category: 'community',
        wardSpecific: ['auburn-gresham'],
        zahir: {
          sensory: 'A community garden in Auburn Gresham — raised beds, a tool shed, neighbors tending plots in the early morning.',
          sight: 'Raised beds, vegetables, flowers, community members.',
          smell: 'Soil, herbs, the smell of a living garden.',
          texture: 'Soft soil, rough wood of raised beds, smooth vegetable skins.',
          sound: 'Community voices, birds, the sounds of a living garden.',
          huduthDhati: 'A community garden — its Huduth Dhati is the convergence of shared labor, ecological cultivation, and community connection into a living space.',
          huduthZamani: 'Vacant lot, then planning, then planting, then harvest. Each stage a Huduth Zamani.'
        },
        sunnatProcesses: [
          {
            processName: 'Community Food Cultivation',
            namesOfAllah: [
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — food grown in community soil' },
              { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'The Gatherer — community gathering around cultivation' }
            ],
            description: 'Community food cultivation is Ar-Razzaq\'s provision expressed through collective human effort — and Al-Jami\'s gathering expressed in the community that forms around a garden.',
          }
        ],
        haqq: {
          zahirStatement: 'A community garden in Auburn Gresham.',
          batinStatement: 'Ar-Razzaq\'s provision expressed in community cultivation — and a sign of the food desert conditions that make this garden necessary. The community garden is both a blessing and a response to injustice.',
          ayat: [{ ref: '16:11', arabic: 'يُنبِتُ لَكُم بِهِ الزَّرْعَ', translation: 'He causes to grow for you with it the crops.' }]
        },
        amanah: {
          statement: 'Support community gardens in Auburn Gresham. Advocate for food justice. Connect cultivation to community health.',
          namesActive: [
            { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'Food provision as Amanah' },
            { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'Community gathering as Amanah' }
          ],
          reshatReference: 'The Caretaker who tends the community garden wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Health', 'Justice']
      }
    ]
  },

  // ── CHICAGO — ROSELAND ────────────────────────────────────────────────────
  {
    wardId: 'roseland',
    wardName: 'Roseland',
    cityId: 'chicago',
    catalogCount: 30,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'roseland-calumet-river',
        name: 'Calumet River Corridor',
        arabicName: 'مَجْرَى نَهْرِ كَالُومِت',
        category: 'water',
        wardSpecific: ['roseland'],
        zahir: {
          sensory: 'The Calumet River near Roseland — industrial history visible in the banks, but also herons, migratory birds, and native plants returning.',
          sight: 'The river, industrial remnants, herons, native plants.',
          smell: 'River water, industrial history, the smell of ecological recovery.',
          texture: 'Rough industrial banks, smooth river water, the texture of a recovering ecosystem.',
          sound: 'River flow, birds, the sounds of an industrial waterway recovering.',
          huduthDhati: 'The Calumet River — its Huduth Dhati is the convergence of industrial history, ecological resilience, and ongoing recovery into a living waterway.',
          huduthZamani: 'Prairie river, then industrial canal, then pollution, then ecological recovery. Each stage a Huduth Zamani.'
        },
        sunnatProcesses: [
          {
            processName: 'Ecological Recovery',
            namesOfAllah: [
              { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'The Giver of Life — life returning to an industrial waterway' },
              { arabic: 'الرَّحِيم', transliteration: 'Ar-Rahim', meaning: 'The Especially Merciful — mercy expressed in ecological recovery' }
            ],
            description: 'Ecological recovery in the Calumet River is Al-Muhyi\'s life-giving expressed in the biology of a recovering waterway — life returning to an industrial corridor.',
          }
        ],
        haqq: {
          zahirStatement: 'The Calumet River near Roseland.',
          batinStatement: 'Al-Muhyi\'s life-giving expressed in ecological recovery — and a sign of the industrial history that shaped Roseland. The Calumet River is both a wound and a healing.',
          ayat: [{ ref: '25:48', arabic: 'وَهُوَ الَّذِي أَرْسَلَ الرِّيَاحَ بُشْرًا بَيْنَ يَدَيْ رَحْمَتِهِ وَأَنزَلْنَا مِنَ السَّمَاءِ مَاءً طَهُورًا', translation: 'And He is the One Who sends the winds as glad tidings before His mercy, and We send down from the sky pure water.' }]
        },
        amanah: {
          statement: 'Support ecological restoration of the Calumet River. Advocate for environmental justice in Roseland. Connect river restoration to community health.',
          namesActive: [
            { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'Ecological recovery as Amanah' },
            { arabic: 'الرَّحِيم', transliteration: 'Ar-Rahim', meaning: 'Environmental justice as Amanah' }
          ],
          reshatReference: 'The Caretaker who advocates for the Calumet River wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Health', 'Justice']
      }
    ]
  },

  // ── CHICAGO — NORTH LAWNDALE ──────────────────────────────────────────────
  {
    wardId: 'north-lawndale',
    wardName: 'North Lawndale',
    cityId: 'chicago',
    catalogCount: 34,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'north-lawndale-greening',
        name: 'North Lawndale Greening Initiative',
        arabicName: 'مُبَادَرَةُ التَّخْضِير',
        category: 'community',
        wardSpecific: ['north-lawndale'],
        zahir: {
          sensory: 'A greening initiative in North Lawndale — vacant lots transformed into green spaces, trees planted along streets, community members involved in the work.',
          sight: 'New trees, green spaces, community members, transformed vacant lots.',
          smell: 'Fresh soil, new plantings, the smell of a neighborhood being restored.',
          texture: 'Soft new soil, rough tree bark, the texture of a neighborhood in transformation.',
          sound: 'Community voices, birds in new trees, the sounds of a neighborhood being restored.',
          huduthDhati: 'A greening initiative — its Huduth Dhati is the convergence of community organizing, ecological restoration, and neighborhood transformation into a living process.',
          huduthZamani: 'Disinvestment, then organizing, then greening, then transformation. Each stage a Huduth Zamani.'
        },
        sunnatProcesses: [
          {
            processName: 'Community-Led Ecological Restoration',
            namesOfAllah: [
              { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'The Giver of Life — life returning to a disinvested neighborhood' },
              { arabic: 'الرَّحِيم', transliteration: 'Ar-Rahim', meaning: 'The Especially Merciful — mercy expressed in community restoration' }
            ],
            description: 'Community-led ecological restoration is Al-Muhyi\'s life-giving expressed through collective human effort — and Ar-Rahim\'s mercy expressed in the transformation of a disinvested neighborhood.',
          }
        ],
        haqq: {
          zahirStatement: 'A greening initiative in North Lawndale.',
          batinStatement: 'Al-Muhyi\'s life-giving expressed in community restoration — and a sign of the disinvestment that made this work necessary. The greening initiative is both a blessing and a response to injustice.',
          ayat: [{ ref: '7:58', arabic: 'وَالْبَلَدُ الطَّيِّبُ يَخْرُجُ نَبَاتُهُ بِإِذْنِ رَبِّهِ', translation: 'And the land — the pure comes forth its vegetation by permission of its Lord.' }]
        },
        amanah: {
          statement: 'Support greening initiatives in North Lawndale. Advocate for equitable investment in neighborhood restoration. Connect ecological restoration to community health.',
          namesActive: [
            { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'Life-giving as Amanah' },
            { arabic: 'الرَّحِيم', transliteration: 'Ar-Rahim', meaning: 'Community restoration as Amanah' }
          ],
          reshatReference: 'The Caretaker who tends the greening initiative wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Health', 'Justice']
      }
    ]
  },

  // ── CHICAGO — BRONZEVILLE ─────────────────────────────────────────────────
  {
    wardId: 'bronzeville',
    wardName: 'Bronzeville',
    cityId: 'chicago',
    catalogCount: 42,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'bronzeville-lakefront',
        name: 'Bronzeville Lakefront (31st Street Harbor)',
        arabicName: 'شَاطِئُ بْرُونْزْفِيل',
        category: 'water',
        wardSpecific: ['bronzeville'],
        zahir: {
          sensory: 'The lakefront at 31st Street in Bronzeville — Lake Michigan stretching to the horizon, the Chicago skyline to the north, the smell of fresh water.',
          sight: 'Lake Michigan, the harbor, the skyline, community members at the water.',
          smell: 'Fresh lake water, the smell of a Great Lake.',
          texture: 'Smooth water, rough concrete of the harbor, the texture of a lakefront.',
          sound: 'Waves, birds, the sounds of a living lakefront.',
          huduthDhati: 'The Bronzeville lakefront — its Huduth Dhati is the convergence of Great Lake ecology, urban history, and community access into a living waterfront.',
          huduthZamani: 'Glacial lake, then prairie shore, then industrial waterfront, then community lakefront. Each stage a Huduth Zamani.'
        },
        sunnatProcesses: [
          {
            processName: 'Great Lake Ecology',
            namesOfAllah: [
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — the Great Lake as provision for the region' },
              { arabic: 'الرَّحِيم', transliteration: 'Ar-Rahim', meaning: 'The Especially Merciful — fresh water as mercy' }
            ],
            description: 'Great Lake ecology is Ar-Razzaq\'s provision expressed in the biology of a freshwater ecosystem — and Ar-Rahim\'s mercy expressed in the gift of fresh water.',
          }
        ],
        haqq: {
          zahirStatement: 'The lakefront at 31st Street in Bronzeville.',
          batinStatement: 'Ar-Razzaq\'s provision expressed in the Great Lake — and a sign of the Great Migration that brought Black Americans to Chicago\'s lakefront neighborhoods. The Bronzeville lakefront is both a blessing and a history.',
          ayat: [{ ref: '55:19-22', arabic: 'مَرَجَ الْبَحْرَيْنِ يَلْتَقِيَانِ بَيْنَهُمَا بَرْزَخٌ لَّا يَبْغِيَانِ', translation: 'He released the two seas, meeting. Between both of them a barrier, not they transgress.' }]
        },
        amanah: {
          statement: 'Protect community access to the Bronzeville lakefront. Advocate for equitable lakefront development. Connect lake ecology to community history.',
          namesActive: [
            { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'Lake provision as Amanah' },
            { arabic: 'الرَّحِيم', transliteration: 'Ar-Rahim', meaning: 'Community access as Amanah' }
          ],
          reshatReference: 'The Caretaker who protects the Bronzeville lakefront wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Health', 'Justice']
      }
    ]
  },

  // ── ATLANTA — PITTSBURGH/MECHANICSVILLE ───────────────────────────────────
  {
    wardId: 'pittsburgh-mechanicsville',
    wardName: 'Pittsburgh / Mechanicsville',
    cityId: 'atlanta',
    catalogCount: 29,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'pittsburgh-atl-urban-farm',
        name: 'Pittsburgh Urban Farm (Lee Street Corridor)',
        arabicName: 'مَزْرَعَةُ الْمَدِينَة',
        category: 'community',
        wardSpecific: ['pittsburgh-mechanicsville'],
        zahir: {
          sensory: 'An urban farm in Pittsburgh/Mechanicsville — raised beds, fruit trees, a hoop house, community members working the land.',
          sight: 'Raised beds, fruit trees, hoop house, community members.',
          smell: 'Soil, herbs, the smell of a living farm in the city.',
          texture: 'Soft soil, rough wood, smooth vegetable skins.',
          sound: 'Community voices, birds, the sounds of a living farm.',
          huduthDhati: 'An urban farm — its Huduth Dhati is the convergence of community organizing, ecological cultivation, and food justice into a living space.',
          huduthZamani: 'Vacant lot, then organizing, then planting, then harvest. Each stage a Huduth Zamani.'
        },
        sunnatProcesses: [
          {
            processName: 'Urban Food Production',
            namesOfAllah: [
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — food grown in community soil' },
              { arabic: 'الرَّحِيم', transliteration: 'Ar-Rahim', meaning: 'The Especially Merciful — food justice as mercy' }
            ],
            description: 'Urban food production is Ar-Razzaq\'s provision expressed through collective human effort — and Ar-Rahim\'s mercy expressed in food justice work.',
          }
        ],
        haqq: {
          zahirStatement: 'An urban farm in Pittsburgh/Mechanicsville, Atlanta.',
          batinStatement: 'Ar-Razzaq\'s provision expressed in community cultivation — and a sign of the food desert conditions that make this farm necessary. The urban farm is both a blessing and a response to injustice.',
          ayat: [{ ref: '16:11', arabic: 'يُنبِتُ لَكُم بِهِ الزَّرْعَ', translation: 'He causes to grow for you with it the crops.' }]
        },
        amanah: {
          statement: 'Support urban farms in Pittsburgh/Mechanicsville. Advocate for food justice. Connect cultivation to community health.',
          namesActive: [
            { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'Food provision as Amanah' },
            { arabic: 'الرَّحِيم', transliteration: 'Ar-Rahim', meaning: 'Food justice as Amanah' }
          ],
          reshatReference: 'The Caretaker who tends the urban farm wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Health', 'Justice']
      }
    ]
  },

  // ── ATLANTA — VINE CITY ───────────────────────────────────────────────────
  {
    wardId: 'vine-city',
    wardName: 'Vine City',
    cityId: 'atlanta',
    catalogCount: 27,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'vine-city-proctor-creek',
        name: 'Proctor Creek Greenway',
        arabicName: 'مَمَرُّ نَهْرِ بْرُوكْتُر',
        category: 'water',
        wardSpecific: ['vine-city'],
        zahir: {
          sensory: 'Proctor Creek in Vine City — a creek that has been channelized and polluted but is being restored, with native plants returning to the banks.',
          sight: 'The creek, native plants, restoration work, community members.',
          smell: 'Creek water, native plants, the smell of ecological recovery.',
          texture: 'Rough creek banks, smooth water, the texture of a recovering waterway.',
          sound: 'Creek flow, birds, the sounds of an urban creek recovering.',
          huduthDhati: 'Proctor Creek — its Huduth Dhati is the convergence of urban hydrology, ecological resilience, and community restoration into a living waterway.',
          huduthZamani: 'Natural creek, then channelization, then pollution, then restoration. Each stage a Huduth Zamani.'
        },
        sunnatProcesses: [
          {
            processName: 'Urban Creek Restoration',
            namesOfAllah: [
              { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'The Giver of Life — life returning to a polluted creek' },
              { arabic: 'الرَّحِيم', transliteration: 'Ar-Rahim', meaning: 'The Especially Merciful — environmental justice as mercy' }
            ],
            description: 'Urban creek restoration is Al-Muhyi\'s life-giving expressed in the biology of a recovering waterway — and Ar-Rahim\'s mercy expressed in environmental justice work.',
          }
        ],
        haqq: {
          zahirStatement: 'Proctor Creek in Vine City, Atlanta.',
          batinStatement: 'Al-Muhyi\'s life-giving expressed in ecological recovery — and a sign of the environmental injustice that polluted this creek. Proctor Creek is both a wound and a healing.',
          ayat: [{ ref: '25:48', arabic: 'وَأَنزَلْنَا مِنَ السَّمَاءِ مَاءً طَهُورًا', translation: 'And We send down from the sky pure water.' }]
        },
        amanah: {
          statement: 'Support Proctor Creek restoration. Advocate for environmental justice in Vine City. Connect creek restoration to community health.',
          namesActive: [
            { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'Ecological recovery as Amanah' },
            { arabic: 'الرَّحِيم', transliteration: 'Ar-Rahim', meaning: 'Environmental justice as Amanah' }
          ],
          reshatReference: 'The Caretaker who advocates for Proctor Creek wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Health', 'Justice']
      }
    ]
  },

  // ── ATLANTA — ENGLISH AVENUE ──────────────────────────────────────────────
  {
    wardId: 'english-avenue',
    wardName: 'English Avenue',
    cityId: 'atlanta',
    catalogCount: 25,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'english-avenue-community-garden',
        name: 'English Avenue Community Garden',
        arabicName: 'حَدِيقَةُ الْمُجْتَمَع',
        category: 'community',
        wardSpecific: ['english-avenue'],
        zahir: {
          sensory: 'A community garden in English Avenue — raised beds, fruit trees, community members tending plots.',
          sight: 'Raised beds, fruit trees, community members.',
          smell: 'Soil, herbs, the smell of a living garden.',
          texture: 'Soft soil, rough wood, smooth vegetable skins.',
          sound: 'Community voices, birds, the sounds of a living garden.',
          huduthDhati: 'A community garden — its Huduth Dhati is the convergence of shared labor, ecological cultivation, and community connection into a living space.',
          huduthZamani: 'Vacant lot, then organizing, then planting, then harvest. Each stage a Huduth Zamani.'
        },
        sunnatProcesses: [
          {
            processName: 'Community Food Cultivation',
            namesOfAllah: [
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — food grown in community soil' },
              { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'The Gatherer — community gathering around cultivation' }
            ],
            description: 'Community food cultivation is Ar-Razzaq\'s provision expressed through collective human effort.',
          }
        ],
        haqq: {
          zahirStatement: 'A community garden in English Avenue, Atlanta.',
          batinStatement: 'Ar-Razzaq\'s provision expressed in community cultivation — and a sign of the food desert conditions that make this garden necessary.',
          ayat: [{ ref: '16:11', arabic: 'يُنبِتُ لَكُم بِهِ الزَّرْعَ', translation: 'He causes to grow for you with it the crops.' }]
        },
        amanah: {
          statement: 'Support community gardens in English Avenue. Advocate for food justice. Connect cultivation to community health.',
          namesActive: [
            { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'Food provision as Amanah' }
          ],
          reshatReference: 'The Caretaker who tends the community garden wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Health', 'Justice']
      }
    ]
  },

  // ── ATLANTA — SUMMERHILL ──────────────────────────────────────────────────
  {
    wardId: 'summerhill',
    wardName: 'Summerhill',
    cityId: 'atlanta',
    catalogCount: 28,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'summerhill-urban-orchard',
        name: 'Summerhill Urban Orchard',
        arabicName: 'بُسْتَانُ الْمَدِينَة',
        category: 'plant',
        wardSpecific: ['summerhill'],
        zahir: {
          sensory: 'An urban orchard in Summerhill — fruit trees planted along streets and in community spaces, fruit available to all.',
          sight: 'Fruit trees, fruit, community members harvesting.',
          smell: 'Ripening fruit, the smell of a living orchard.',
          texture: 'Rough bark, smooth fruit skin.',
          sound: 'Birds in fruit trees, community voices.',
          huduthDhati: 'An urban orchard — its Huduth Dhati is the convergence of community organizing, fruit tree cultivation, and food access into a living space.',
          huduthZamani: 'Vacant lots, then planting, then growth, then harvest. Each stage a Huduth Zamani.'
        },
        sunnatProcesses: [
          {
            processName: 'Fruit Tree Cultivation',
            namesOfAllah: [
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — fruit as provision for all who pass' },
              { arabic: 'الْوَهَّاب', transliteration: 'Al-Wahhab', meaning: 'The Bestower — fruit given freely, without condition' }
            ],
            description: 'Fruit tree cultivation is Ar-Razzaq\'s provision expressed through community effort — and Al-Wahhab\'s generosity expressed in fruit given freely.',
          }
        ],
        haqq: {
          zahirStatement: 'An urban orchard in Summerhill, Atlanta.',
          batinStatement: 'Ar-Razzaq\'s provision expressed in community cultivation — and a sign of the food access challenges that make this orchard necessary.',
          ayat: [{ ref: '16:11', arabic: 'وَالنَّخِيلَ وَالْأَعْنَابَ وَمِن كُلِّ الثَّمَرَاتِ', translation: 'And the date palms and the grapes and of every kind fruits.' }]
        },
        amanah: {
          statement: 'Support urban orchards in Summerhill. Advocate for food access. Connect fruit cultivation to community health.',
          namesActive: [
            { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'Fruit provision as Amanah' }
          ],
          reshatReference: 'The Caretaker who tends the urban orchard wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Health', 'Justice']
      }
    ]
  },

  // ── ATLANTA — PEOPLESTOWN ─────────────────────────────────────────────────
  {
    wardId: 'peoplestown',
    wardName: 'Peoplestown',
    cityId: 'atlanta',
    catalogCount: 26,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'peoplestown-intrenchment-creek',
        name: 'Intrenchment Creek Park',
        arabicName: 'حَدِيقَةُ نَهْرِ إِنْتْرِنْشْمِنْت',
        category: 'water',
        wardSpecific: ['peoplestown'],
        zahir: {
          sensory: 'Intrenchment Creek in Peoplestown — a creek corridor with native plants, wildlife, and community green space.',
          sight: 'The creek, native plants, wildlife, community members.',
          smell: 'Creek water, native plants, the smell of a living corridor.',
          texture: 'Rough creek banks, smooth water, the texture of a living corridor.',
          sound: 'Creek flow, birds, the sounds of a living waterway.',
          huduthDhati: 'Intrenchment Creek — its Huduth Dhati is the convergence of urban hydrology, ecological resilience, and community green space into a living corridor.',
          huduthZamani: 'Natural creek, then urban development, then ecological restoration. Each stage a Huduth Zamani.'
        },
        sunnatProcesses: [
          {
            processName: 'Urban Creek Ecology',
            namesOfAllah: [
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — creek as provision for wildlife and community' },
              { arabic: 'الرَّحِيم', transliteration: 'Ar-Rahim', meaning: 'The Especially Merciful — green space as mercy' }
            ],
            description: 'Urban creek ecology is Ar-Razzaq\'s provision expressed in the biology of a living waterway — and Ar-Rahim\'s mercy expressed in community green space.',
          }
        ],
        haqq: {
          zahirStatement: 'Intrenchment Creek in Peoplestown, Atlanta.',
          batinStatement: 'Ar-Razzaq\'s provision expressed in creek ecology — and a sign of the community\'s fight to protect this green space from development.',
          ayat: [{ ref: '25:48', arabic: 'وَأَنزَلْنَا مِنَ السَّمَاءِ مَاءً طَهُورًا', translation: 'And We send down from the sky pure water.' }]
        },
        amanah: {
          statement: 'Support protection of Intrenchment Creek. Advocate for community green space. Connect creek ecology to community health.',
          namesActive: [
            { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'Creek provision as Amanah' }
          ],
          reshatReference: 'The Caretaker who protects Intrenchment Creek wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Health', 'Justice']
      }
    ]
  },

  // ── ATLANTA — EDGEWOOD ────────────────────────────────────────────────────
  {
    wardId: 'edgewood-atl',
    wardName: 'Edgewood',
    cityId: 'atlanta',
    catalogCount: 31,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'edgewood-beltline-east',
        name: 'BeltLine Eastside Trail (Edgewood Segment)',
        arabicName: 'مَمَرُّ الْحَزَام الشَّرْقِي',
        category: 'community',
        wardSpecific: ['edgewood-atl'],
        zahir: {
          sensory: 'The BeltLine Eastside Trail through Edgewood — a walking and cycling path connecting neighborhoods, with murals, gardens, and community spaces.',
          sight: 'The trail, murals, gardens, community members.',
          smell: 'Trees, the smell of a green corridor.',
          texture: 'Smooth pavement, rough tree bark.',
          sound: 'Community voices, birds, the sounds of a living trail.',
          huduthDhati: 'The BeltLine Eastside Trail — its Huduth Dhati is the convergence of former rail infrastructure, ecological restoration, and community connection into a living pathway.',
          huduthZamani: 'Rail corridor, then abandonment, then transformation, then community trail. Each stage a Huduth Zamani.'
        },
        sunnatProcesses: [
          {
            processName: 'Urban Green Corridor',
            namesOfAllah: [
              { arabic: 'الرَّحِيم', transliteration: 'Ar-Rahim', meaning: 'The Especially Merciful — green space as mercy' },
              { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'The Gatherer — trail gathering community' }
            ],
            description: 'The BeltLine trail is a Sunnat of Al-Jami — community gathering expressed in a shared green corridor.',
          }
        ],
        haqq: {
          zahirStatement: 'The BeltLine Eastside Trail through Edgewood, Atlanta.',
          batinStatement: 'Al-Jami\'s gathering expressed in a shared green corridor — and a question about equitable development along the BeltLine.',
          ayat: [{ ref: '25:63', arabic: 'وَعِبَادُ الرَّحْمَٰنِ الَّذِينَ يَمْشُونَ عَلَى الْأَرْضِ هَوْنًا', translation: 'And the servants of the Most Merciful are those who walk upon the earth easily.' }]
        },
        amanah: {
          statement: 'Support equitable development along the BeltLine in Edgewood. Ensure community members benefit from the trail.',
          namesActive: [
            { arabic: 'الرَّحِيم', transliteration: 'Ar-Rahim', meaning: 'Green space as Amanah' }
          ],
          reshatReference: 'The Caretaker who walks the BeltLine with intention wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Health', 'Justice']
      }
    ]
  },
  // ── CINCINNATI — AVONDALE ─────────────────────────────────────────────────
  {
    wardId: 'avondale',
    wardName: 'Avondale',
    cityId: 'cincinnati',
    catalogCount: 31,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'ohio-river-watershed',
        name: 'Ohio River (Great River Watershed)',
        arabicName: 'نَهْرُ أُوهَايُو',
        category: 'water',
        wardSpecific: ['avondale'],
        zahir: {
          sensory: 'The Ohio River visible from Avondale — the great river that shaped Cincinnati\'s history and ecology.',
          sight: 'The river, the bridges, the Kentucky shore, the water.',
          smell: 'River water, the smell of a great river.',
          texture: 'Water, river banks, the texture of a living river.',
          sound: 'River flow, boats, the sounds of a great river.',
          huduthDhati: 'The Ohio River — its Huduth Dhati is the convergence of watershed ecology, industrial history, and community life into a river that has shaped the region.',
          huduthZamani: 'Glacial formation, then ecological succession, then Native American settlement, then European settlement, then industrial use, then restoration. Each stage a Huduth Zamani.'
        },
        sunnatProcesses: [
          {
            processName: 'River Ecology',
            namesOfAllah: [
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — river as provision for the region' },
              { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'The Giver of Life — river as source of life' }
            ],
            description: 'The Ohio River watershed provides water, food, and ecological services for millions — a Sunnat of Ar-Razzaq operating at a regional scale.',
          }
        ],
        haqq: {
          zahirStatement: 'The Ohio River from Avondale.',
          batinStatement: 'Ar-Razzaq\'s provision at a regional scale — and a question about who has access to this provision. The river is both a blessing and a responsibility.',
          ayat: [
            { ref: '21:30', arabic: 'وَجَعَلْنَا مِنَ الْمَاءِ كُلَّ شَيْءٍ حَيٍّ', translation: 'And We made from water every living thing.' }
          ]
        },
        amanah: {
          statement: 'Protect the Ohio River watershed. Ensure Avondale residents have access to the river and its provision.',
          namesActive: [
            { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'River provision as Amanah' },
            { arabic: 'الْحَافِظ', transliteration: 'Al-Hafiz', meaning: 'Protecting the watershed' }
          ],
          reshatReference: 'The Caretaker who protects the Ohio River wears a thread of Taqwah.'
        },
        resourceCategories: ['Health', 'Community', 'Justice']
      }
    ]
  },
  // ── CINCINNATI — OVER-THE-RHINE ───────────────────────────────────────────
  {
    wardId: 'over-the-rhine',
    wardName: 'Over-the-Rhine',
    cityId: 'cincinnati',
    catalogCount: 28,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'otr-architecture',
        name: 'Historical Italianate Architecture',
        arabicName: 'الْعِمَارَةُ التَّارِيخِيَّة',
        category: 'structure',
        wardSpecific: ['over-the-rhine'],
        zahir: {
          sensory: 'The Italianate architecture of Over-the-Rhine — 19th century buildings preserved through decades of disinvestment, now at the center of gentrification.',
          sight: 'Historic facades, ornate details, the contrast between preservation and displacement.',
          smell: 'Old brick, the smell of a historic neighborhood.',
          texture: 'Rough brick, smooth stone, the texture of preserved history.',
          sound: 'The sounds of a neighborhood in transformation.',
          huduthDhati: 'Historic architecture — its Huduth Dhati is both physical (the buildings) and social (the community that lived in them). Al-Baqi\'s attribute of endurance is manifest in the buildings that outlast their builders.',
          huduthZamani: 'German immigrant settlement, then Black community, then disinvestment, then preservation, then gentrification. Each stage a Huduth Zamani.'
        },
        sunnatProcesses: [
          {
            processName: 'Architectural Endurance',
            namesOfAllah: [
              { arabic: 'الْبَاقِي', transliteration: 'Al-Baqi', meaning: 'The Everlasting — buildings outlasting their builders' },
              { arabic: 'الشَّهِيد', transliteration: 'Al-Shahid', meaning: 'The Witness — architecture bearing witness to history' }
            ],
            description: 'The Italianate buildings of Over-the-Rhine are a Sunnat of Al-Baqi — they outlast the communities that built them and the communities that lived in them.',
          }
        ],
        haqq: {
          zahirStatement: 'The Italianate architecture of Over-the-Rhine.',
          batinStatement: 'A question about what endures and what is lost. The buildings are Al-Baqi\'s sign — they outlast their builders. But the community that lived in them is being displaced. The Haqq is that beautiful architecture without community justice is incomplete.',
          ayat: [
            { ref: '28:58', arabic: 'وَكَمْ أَهْلَكْنَا مِن قَرْيَةٍ بَطِرَتْ مَعِيشَتَهَا', translation: 'And how many a city have We destroyed that was insolent in its [way of] living.' },
            { ref: '22:45', arabic: 'فَكَأَيِّن مِّن قَرْيَةٍ أَهْلَكْنَاهَا وَهِيَ ظَالِمَةٌ فَهِيَ خَاوِيَةٌ عَلَىٰ عُرُوشِهَا', translation: 'And how many a city did We destroy while it was committing wrong — so it is fallen into ruin.' }
          ]
        },
        amanah: {
          statement: 'Advocate for community land trusts in Over-the-Rhine. Ensure that historic preservation includes community preservation.',
          namesActive: [
            { arabic: 'الْعَدْل', transliteration: 'Al-Adl', meaning: 'Justice in preservation' },
            { arabic: 'الشَّهِيد', transliteration: 'Al-Shahid', meaning: 'Bearing witness to displacement' }
          ],
          reshatReference: 'The Caretaker who advocates for community preservation wears a thread of Taqwah.'
        },
        resourceCategories: ['Housing', 'Justice', 'Community']
      }
    ]
  },
  // ── DETROIT — BRIGHTMOOR ─────────────────────────────────────────────────
  {
    wardId: 'brightmoor',
    wardName: 'Brightmoor',
    cityId: 'detroit',
    catalogCount: 34,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'brightmoor-prairie',
        name: 'Prairie Succession (Vacant Lots Rewilding)',
        arabicName: 'تَعَاقُبُ السَّهْل',
        category: 'plant',
        wardSpecific: ['brightmoor'],
        zahir: {
          sensory: 'Vacant lots in Brightmoor returning to tallgrass prairie — native grasses re-establishing where houses once stood.',
          sight: 'Tall grasses, wildflowers, birds. The absence of structures where structures once were.',
          smell: 'Dry earth, native grasses, the faint sweetness of wildflowers.',
          texture: 'Rough grass stems, loose soil, seed heads.',
          sound: 'Wind through tall grass, insects, birds that have returned.',
          huduthDhati: 'Ecological succession proceeding in the absence of human intervention — annuals first, then perennials, then woody shrubs.',
          huduthZamani: 'Brightmoor lost 30%+ of its housing stock through abandonment and demolition. The prairie succession began when the structures were removed.'
        },
        sunnatProcesses: [
          {
            processName: 'Ecological Succession',
            namesOfAllah: [
              { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'The Giver of Life — life returning to abandoned land' },
              { arabic: 'الْخَالِق', transliteration: 'Al-Khaliq', meaning: 'The Creator — new ecological forms emerging' }
            ],
            description: 'Pioneer species colonize first — dandelions, ragweed, crabgrass. Then native perennials establish. Then woody shrubs. Each stage prepares the soil for the next.',
          },
          {
            processName: 'Soil Microbiome Restoration',
            namesOfAllah: [
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — soil fertility as provision for future generations' }
            ],
            description: 'As native plants establish, the soil microbiome recovers — bacteria, fungi, and invertebrates returning to compact, depleted urban soil.',
          }
        ],
        haqq: {
          zahirStatement: 'Vacant lots in Brightmoor returning to prairie.',
          batinStatement: 'A sign of Al-Muhyi operating in the absence of human action — and a question about what restoration looks like when the zahir has been taken.',
          ayat: [
            { ref: '22:5', arabic: 'وَتَرَى الْأَرْضَ هَامِدَةً فَإِذَا أَنزَلْنَا عَلَيْهَا الْمَاءَ اهْتَزَّتْ وَرَبَتْ', translation: 'You see the earth barren, but when We send down rain upon it, it quivers and swells.' }
          ]
        },
        amanah: {
          statement: 'Brightmoor Farmway collective maintains paths through the rewilding, connecting residents to the succession.',
          namesActive: [
            { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'Life returning to abandoned land' },
            { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'Provision through ecological restoration' }
          ],
          reshatReference: 'The Caretaker who plants native prairie grass in a vacant lot wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Food', 'Education']
      }
    ]
  },
  // ── DETROIT — BANGLATOWN ─────────────────────────────────────────────────
  {
    wardId: 'banglatown',
    wardName: 'Banglatown',
    cityId: 'detroit',
    catalogCount: 28,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'banglatown-rizq',
        name: 'Halal Market Corridor (Conant Street)',
        arabicName: 'مَمَرُّ الرِّزْق',
        category: 'community',
        wardSpecific: ['banglatown'],
        zahir: {
          sensory: 'Halal markets on Conant Street — Bangladeshi, Yemeni, Bosnian grocers side by side, the smell of spices and fresh produce.',
          sight: 'Colorful storefronts, Arabic and Bengali signage, produce displays on the sidewalk.',
          smell: 'Spices, fresh produce, the smell of community provision.',
          texture: 'Rough produce, smooth packaging, the texture of a working market.',
          sound: 'Multiple languages, the sounds of commerce and community.',
          huduthDhati: 'A provision corridor — its Huduth Dhati is the convergence of immigrant communities around shared need.',
          huduthZamani: 'Immigration, then settlement, then commerce, then community institution. Each stage a Huduth Zamani.'
        },
        sunnatProcesses: [
          {
            processName: 'Cultural Ecology of Provision',
            namesOfAllah: [
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — Rizq flowing through immigrant hands' },
              { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'The Gatherer — bringing what was scattered into community' }
            ],
            description: 'The halal market corridor is a Sunnat of Al-Jami — the gathering of people around shared provision, shared language, and shared culture.',
          }
        ],
        haqq: {
          zahirStatement: 'Halal markets on Conant Street in Banglatown.',
          batinStatement: 'Ar-Razzaq\'s provision flowing through human hands — the corridor is a sentence in the grammar of provision.',
          ayat: [
            { ref: '2:172', arabic: 'كُلُوا مِن طَيِّبَاتِ مَا رَزَقْنَاكُمْ', translation: 'Eat of the good things We have provided for you.' }
          ]
        },
        amanah: {
          statement: 'Banglatown merchants association operates informal mutual aid during Ramadan. Support and strengthen this corridor.',
          namesActive: [
            { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'Provision being actively maintained' },
            { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'Community gathering around shared provision' }
          ],
          reshatReference: 'The Caretaker who sees the halal market as Ar-Razzaq\'s provision wears a thread of Taqwah.'
        },
        resourceCategories: ['Food', 'Community', 'Income']
      }
    ]
  },
  // ── DETROIT — GRATIOT / 7 MILE ────────────────────────────────────────────
  {
    wardId: 'gratiot-7mile',
    wardName: 'Gratiot / 7 Mile',
    cityId: 'detroit',
    catalogCount: 28,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'masjid-muath-gratiot',
        name: 'Masjid Mu\'ath on Gratiot Avenue',
        arabicName: 'مَسْجِدُ مُعَاذ',
        category: 'community',
        wardSpecific: ['gratiot-7mile'],
        zahir: {
          sensory: 'Masjid Mu\'ath on Gratiot Avenue — five decades of continuity through demographic shift. The adhan persisting across neighborhood transformation.',
          sight: 'The masjid building, the congregation gathering, the signs of institutional persistence.',
          smell: 'The smell of a Muslim community — incense, community, prayer.',
          texture: 'The texture of an institution that has endured.',
          sound: 'Adhan, Quran recitation, community voices across five decades.',
          huduthDhati: 'A masjid as community anchor — its Huduth Dhati is both the physical (the structure) and the spiritual community it holds.',
          huduthZamani: 'Masjid Mu\'ath has served the Gratiot corridor for five decades through demographic shifts. Its persistence is a sign of Al-Baqi.'
        },
        sunnatProcesses: [
          {
            processName: 'Institutional Persistence',
            namesOfAllah: [
              { arabic: 'الْبَاقِي', transliteration: 'Al-Baqi', meaning: 'The Everlasting — institution persisting through change' },
              { arabic: 'الصَّابِر', transliteration: 'As-Sabur', meaning: 'The Patient — five decades of continuity' }
            ],
            description: 'Masjid Mu\'ath\'s persistence through demographic shift is a Sunnat of Al-Baqi — the institution that endures when everything around it changes.',
          },
          {
            processName: 'Friday Gathering',
            namesOfAllah: [
              { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'The Gatherer — Friday prayer as community gathering' }
            ],
            description: 'The Friday gathering at Masjid Mu\'ath weaves the community together across demographic shifts — Al-Jami manifest in the Jumu\'ah.',
          },
          {
            processName: 'Provision Through Endurance',
            namesOfAllah: [
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — provision sustained through endurance' }
            ],
            description: 'The masjid provides spiritual and community provision through decades of endurance — Ar-Razzaq flowing through institutional persistence.',
          }
        ],
        haqq: {
          zahirStatement: 'Masjid Mu\'ath on Gratiot Avenue, Detroit.',
          batinStatement: 'Al-Baqi manifest in institutional persistence — and Al-Jami expressed through the Friday gathering that weaves community across demographic shift.',
          ayat: [
            { ref: '16:96', arabic: 'مَا عِندَكُمْ يَنفَدُ ۖ وَمَا عِندَ اللَّهِ بَاقٍ', translation: 'Whatever is with you will end, but what is with Allah endures.' },
            { ref: '62:9', arabic: 'يَا أَيُّهَا الَّذِينَ آمَنُوا إِذَا نُودِيَ لِلصَّلَاةِ مِن يَوْمِ الْجُمُعَةِ فَاسْعَوْا إِلَىٰ ذِكْرِ اللَّهِ', translation: 'O you who believe, when the call to prayer is made on Friday, hasten to the remembrance of Allah.' }
          ]
        },
        amanah: {
          statement: 'Support Masjid Mu\'ath and the Gratiot corridor Muslim community. Connect to Islamic community development and social service resources.',
          namesActive: [
            { arabic: 'الْبَاقِي', transliteration: 'Al-Baqi', meaning: 'Masjid persistence as Amanah' },
            { arabic: 'الصَّابِر', transliteration: 'As-Sabur', meaning: 'Patient persistence through transformation' }
          ],
          reshatReference: 'The Caretaker who supports the masjid\'s persistence wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Education', 'Health']
      }
    ]
  },
  // ── DETROIT — EAST SIDE ───────────────────────────────────────────────────
  {
    wardId: 'east-side-detroit',
    wardName: 'East Side',
    cityId: 'detroit',
    catalogCount: 22,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'heidelberg-project',
        name: 'The Heidelberg Project',
        arabicName: 'مَشْرُوعُ هَايْدِلْبَرْغ',
        category: 'community',
        wardSpecific: ['east-side-detroit'],
        zahir: {
          sensory: 'Abandoned houses transformed into art installations documenting neighborhood reality — polka dots, found objects, and community memory made visible.',
          sight: 'Houses covered in art, found objects, polka dots, the signs of creative resistance.',
          smell: 'Paint, wood, the smell of a living art installation.',
          texture: 'Rough wood, paint, found objects.',
          sound: 'Community voices, the sounds of an outdoor art space.',
          huduthDhati: 'An art installation as community documentation — its Huduth Dhati is both the physical (the houses, the objects) and the conceptual (the truth it tells).',
          huduthZamani: 'Tyree Guyton began the Heidelberg Project in 1986. Its persistence through demolition attempts is a sign of Al-Haqq.'
        },
        sunnatProcesses: [
          {
            processName: 'Truth Through Art',
            namesOfAllah: [
              { arabic: 'الْحَق', transliteration: 'Al-Haqq', meaning: 'The Truth — art as truth-telling' },
              { arabic: 'الشَّهِيد', transliteration: 'Al-Shahid', meaning: 'The Witness — bearing witness through creative expression' }
            ],
            description: 'The Heidelberg Project bears witness to neighborhood reality through art — Al-Haqq expressed through creative documentation.',
          },
          {
            processName: 'Creative Resistance',
            namesOfAllah: [
              { arabic: 'الصَّابِر', transliteration: 'As-Sabur', meaning: 'The Patient — persistence through demolition attempts' }
            ],
            description: 'The project has survived multiple demolition attempts — As-Sabur manifest in creative persistence.',
          },
          {
            processName: 'Community Memory',
            namesOfAllah: [
              { arabic: 'الشَّهِيد', transliteration: 'Al-Shahid', meaning: 'The Witness — preserving community memory' }
            ],
            description: 'The Heidelberg Project preserves the memory of what this neighborhood was — Al-Shahid in the form of art.',
          }
        ],
        haqq: {
          zahirStatement: 'The Heidelberg Project on Detroit\'s East Side.',
          batinStatement: 'Al-Haqq manifest in truth-telling through art — and Al-Shahid expressed through community memory preserved in creative form.',
          ayat: [
            { ref: '6:38', arabic: 'وَمَا مِن دَابَّةٍ فِي الْأَرْضِ وَلَا طَائِرٍ يَطِيرُ بِجَنَاحَيْهِ إِلَّا أُمَمٌ أَمْثَالُكُم ۚ مَّا فَرَّطْنَا فِي الْكِتَابِ مِن شَيْءٍ', translation: 'We have not neglected in the Register a thing.' }
          ]
        },
        amanah: {
          statement: 'Support the Heidelberg Project and East Side community arts. Connect to community arts and neighborhood documentation resources.',
          namesActive: [
            { arabic: 'الْحَق', transliteration: 'Al-Haqq', meaning: 'Truth-telling as Amanah' },
            { arabic: 'الشَّهِيد', transliteration: 'Al-Shahid', meaning: 'Bearing witness through art' }
          ],
          reshatReference: 'The Caretaker who sees the Heidelberg Project as Al-Haqq wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Education', 'Justice']
      }
    ]
  },
  // ── DETROIT — OSBORN ──────────────────────────────────────────────────────
  {
    wardId: 'osborn',
    wardName: 'Osborn',
    cityId: 'detroit',
    catalogCount: 19,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'osborn-transit-desert',
        name: 'Osborn Transit Desert',
        arabicName: 'صَحْرَاءُ النَّقْل',
        category: 'structure',
        wardSpecific: ['osborn'],
        zahir: {
          sensory: '90-minute bus commute to jobs 15 minutes away by car. The route map is a map of opportunity — and its gaps are a map of injustice.',
          sight: 'Bus stops, long waits, the route map showing the gap between Osborn and employment centers.',
          smell: 'Bus exhaust, the smell of a long commute.',
          texture: 'Hard bus seats, the texture of a long journey.',
          sound: 'Bus engines, waiting, the sounds of a transit desert.',
          huduthDhati: 'A transit desert — its Huduth Dhati is the absence of adequate public transportation in a community that needs it most.',
          huduthZamani: 'Osborn\'s transit desert is the result of decades of disinvestment in public transportation for Black communities.'
        },
        sunnatProcesses: [
          {
            processName: 'Transit Equity',
            namesOfAllah: [
              { arabic: 'الْعَدْل', transliteration: 'Al-Adl', meaning: 'The Just — transit access as justice' },
              { arabic: 'الصَّابِر', transliteration: 'As-Sabur', meaning: 'The Patient — enduring the long commute' }
            ],
            description: 'The 90-minute commute is a Sunnat of Al-Adl\'s absence — infrastructure inequity made visible in time.',
          },
          {
            processName: 'Commute Ecology',
            namesOfAllah: [
              { arabic: 'الشَّهِيد', transliteration: 'Al-Shahid', meaning: 'The Witness — the commute as testimony to inequity' }
            ],
            description: 'The commute map is a map of opportunity — and its gaps are testimony to structural injustice.',
          },
          {
            processName: 'Infrastructure Justice',
            namesOfAllah: [
              { arabic: 'الْمُقْسِط', transliteration: 'Al-Muqsit', meaning: 'The Equitable — equitable infrastructure as Amanah' }
            ],
            description: 'Infrastructure equity is a form of Al-Adl — the Caretaker who sees the transit desert as injustice wears a thread of Taqwah.',
          }
        ],
        haqq: {
          zahirStatement: 'Osborn transit desert in Detroit.',
          batinStatement: 'Al-Adl called upon in the gap between the 90-minute bus commute and the 15-minute car commute.',
          ayat: [
            { ref: '4:135', arabic: 'كُونُوا قَوَّامِينَ بِالْقِسْطِ شُهَدَاءَ لِلَّهِ', translation: 'Be persistently standing firm in justice, witnesses for Allah.' }
          ]
        },
        amanah: {
          statement: 'Support transit equity in Osborn. Connect to transportation justice and community development resources.',
          namesActive: [
            { arabic: 'الْعَدْل', transliteration: 'Al-Adl', meaning: 'Transit equity as justice' },
            { arabic: 'الصَّابِر', transliteration: 'As-Sabur', meaning: 'Patient advocacy for change' }
          ],
          reshatReference: 'The Caretaker who sees the transit desert as injustice wears a thread of Taqwah.'
        },
        resourceCategories: ['Income', 'Justice', 'Community']
      }
    ]
  },
  // ── DETROIT — SOUTHWEST ───────────────────────────────────────────────────
  {
    wardId: 'southwest',
    wardName: 'Southwest Detroit',
    cityId: 'detroit',
    catalogCount: 31,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'rouge-river-southwest',
        name: 'The Rouge River Industrial Watershed',
        arabicName: 'وَادِي نَهْرِ رُوج',
        category: 'water',
        wardSpecific: ['southwest'],
        zahir: {
          sensory: 'The Rouge River carries industrial history in its sediment — heavy metals and the return of fish species. A river healing from a century of industrial use.',
          sight: 'The river, industrial infrastructure, returning wildlife, community along the banks.',
          smell: 'River water, industrial residue, the smell of ecological recovery.',
          texture: 'River sediment, industrial banks, the texture of a healing watershed.',
          sound: 'River flow, returning birds, the sounds of ecological recovery.',
          huduthDhati: 'A river as industrial witness — its Huduth Dhati is both the physical (the water, the sediment) and the historical (the industrial legacy it carries).',
          huduthZamani: 'The Rouge River was one of the most polluted rivers in America. Its gradual recovery is a Huduth Zamani of ecological healing.'
        },
        sunnatProcesses: [
          {
            processName: 'Watershed Testimony',
            namesOfAllah: [
              { arabic: 'الْبَصِير', transliteration: 'Al-Basir', meaning: 'The All-Seeing — the river as ecological witness' },
              { arabic: 'الشَّهِيد', transliteration: 'Al-Shahid', meaning: 'The Witness — industrial history in sediment' }
            ],
            description: 'The Rouge River\'s sediment bears witness to industrial history — Al-Basir seeing what human eyes overlook.',
          },
          {
            processName: 'Industrial Ecology',
            namesOfAllah: [
              { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'The Life-Giver — ecological recovery' }
            ],
            description: 'Fish returning to the Rouge River is Al-Muhyi manifest — life returning where it was driven out.',
          },
          {
            processName: 'Water Justice',
            namesOfAllah: [
              { arabic: 'الْعَدْل', transliteration: 'Al-Adl', meaning: 'The Just — clean water as justice' }
            ],
            description: 'Clean water access is a form of Al-Adl — the Caretaker who advocates for watershed health practices Amanah.',
          }
        ],
        haqq: {
          zahirStatement: 'The Rouge River in Southwest Detroit.',
          batinStatement: 'Al-Basir manifest in the river as ecological witness — and Al-Muhyi expressed in the return of life to a healing watershed.',
          ayat: [
            { ref: '21:30', arabic: 'وَجَعَلْنَا مِنَ الْمَاءِ كُلَّ شَيْءٍ حَيٍّ', translation: 'And We made from water every living thing.' }
          ]
        },
        amanah: {
          statement: 'Support Rouge River watershed restoration. Connect to environmental justice and water quality resources in Southwest Detroit.',
          namesActive: [
            { arabic: 'الْبَصِير', transliteration: 'Al-Basir', meaning: 'Ecological witness as Amanah' },
            { arabic: 'الشَّهِيد', transliteration: 'Al-Shahid', meaning: 'Industrial history testimony' }
          ],
          reshatReference: 'The Caretaker who sees the Rouge River as Al-Muhyi wears a thread of Taqwah.'
        },
        resourceCategories: ['Health', 'Justice', 'Community']
      }
    ]
  },
  // ── DETROIT — MEXICANTOWN ─────────────────────────────────────────────────
  {
    wardId: 'mexicantown',
    wardName: 'Mexicantown / Vernor',
    cityId: 'detroit',
    catalogCount: 24,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'dearborn-arab-community',
        name: 'Dearborn Arab American Provision Corridor',
        arabicName: 'مَمَرُّ الرِّزْق الْعَرَبِيّ',
        category: 'community',
        wardSpecific: ['mexicantown'],
        zahir: {
          sensory: 'The largest Arab American community outside the Middle East — halal provision on Warren Avenue. Bakeries, butchers, restaurants, and community institutions provisioning a diaspora.',
          sight: 'Arabic signage, halal markets, community gathering spaces.',
          smell: 'Halal meat, Arabic bread, the smell of a diaspora food corridor.',
          texture: 'The texture of a community that has built provision from scratch in a new land.',
          sound: 'Arabic language, community voices, the sounds of a diaspora marketplace.',
          huduthDhati: 'A diaspora provision corridor — its Huduth Dhati is both the physical (the markets, the institutions) and the communal (the networks of provision).',
          huduthZamani: 'The Dearborn Arab American community has built its provision infrastructure over decades of immigration.'
        },
        sunnatProcesses: [
          {
            processName: 'Diaspora Provision',
            namesOfAllah: [
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — halal provision in diaspora' },
              { arabic: 'الْحَق', transliteration: 'Al-Haqq', meaning: 'The Truth — authentic cultural provision' }
            ],
            description: 'The halal provision corridor is Ar-Razzaq manifest — divine provision flowing through human hands in diaspora.',
          },
          {
            processName: 'Cultural Rizq',
            namesOfAllah: [
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — cultural provision as Rizq' }
            ],
            description: 'Cultural food provision is a form of Rizq — the community that maintains its food traditions maintains its identity.',
          }
        ],
        haqq: {
          zahirStatement: 'The Arab American provision corridor in Dearborn/Southwest Detroit.',
          batinStatement: 'Ar-Razzaq manifest in diaspora provision — and Al-Haqq expressed in the authentic cultural identity maintained through food and community.',
          ayat: [
            { ref: '2:172', arabic: 'كُلُوا مِن طَيِّبَاتِ مَا رَزَقْنَاكُمْ', translation: 'Eat of the good things We have provided for you.' }
          ]
        },
        amanah: {
          statement: 'Support Arab American community institutions in Dearborn. Connect to halal provision networks and cultural community resources.',
          namesActive: [
            { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'Halal provision as Amanah' },
            { arabic: 'الْحَق', transliteration: 'Al-Haqq', meaning: 'Cultural authenticity as truth' }
          ],
          reshatReference: 'The Caretaker who supports diaspora provision wears a thread of Taqwah.'
        },
        resourceCategories: ['Food', 'Community', 'Justice']
      }
    ]
  },
  // ── PITTSBURGH — HILL DISTRICT ────────────────────────────────────────────
  {
    wardId: 'hill-district',
    wardName: 'Hill District',
    cityId: 'pittsburgh',
    catalogCount: 41,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'hill-district-masjid',
        name: 'Masjid Persistence Through Urban Renewal',
        arabicName: 'صُمُودُ الْمَسْجِد',
        category: 'community',
        wardSpecific: ['hill-district'],
        zahir: {
          sensory: 'A masjid standing in the Hill District — persisting through urban renewal, highway construction, and decades of disinvestment.',
          sight: 'The masjid building, the community gathering, the call to prayer in a neighborhood that has been systematically dismantled.',
          smell: 'Incense, the smell of community.',
          texture: 'Worn carpet, smooth prayer beads, the texture of a community that has held on.',
          sound: 'Adhan, Quran recitation, community conversation.',
          huduthDhati: 'An institution of worship and community — its Huduth Dhati is both physical (the building) and social (the congregation).',
          huduthZamani: 'Urban renewal demolished 8,000 homes in the Hill District in the 1950s-60s. The masajid that survived are witnesses to what was here and what was taken.'
        },
        sunnatProcesses: [
          {
            processName: 'Institutional Survival',
            namesOfAllah: [
              { arabic: 'الْبَاقِي', transliteration: 'Al-Baqi', meaning: 'The Everlasting — the institution that outlasts displacement' },
              { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'The Life-Giver — community life persisting through industry\'s decline' }
            ],
            description: 'The masjid persists through urban renewal, highway construction, and disinvestment — a Sunnat of Al-Baqi manifest in institutional survival.',
          },
          {
            processName: 'Community Planning as Amanah',
            namesOfAllah: [
              { arabic: 'الْعَدْل', transliteration: 'Al-Adl', meaning: 'The Just — advocacy for justice as Amanah' }
            ],
            description: 'Hill District masjid hosts community planning sessions — the institution as community infrastructure, not just worship space.',
          }
        ],
        haqq: {
          zahirStatement: 'A masjid standing in the Hill District.',
          batinStatement: 'Al-Baqi manifest in institutional survival — and a question about what was lost. 8,000 homes demolished.',
          ayat: [
            { ref: '16:96', arabic: 'مَا عِندَكُمْ يَنفَدُ ۖ وَمَا عِندَ اللَّهِ بَاقٍ', translation: 'Whatever is with you will end, but what is with Allah endures.' }
          ]
        },
        amanah: {
          statement: 'Hill District masjid hosts community planning sessions. Support the masjid as an anchor of community restoration.',
          namesActive: [
            { arabic: 'الْبَاقِي', transliteration: 'Al-Baqi', meaning: 'Institutional endurance as divine sign' },
            { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'Community life as life-giving' }
          ],
          reshatReference: 'The Caretaker who sees the standing masjid as Al-Baqi\'s sign wears a thread of Taqwah.'
        },
        resourceCategories: ['Housing', 'Community', 'Justice']
      }
    ]
  },
  // ── PITTSBURGH — HOMEWOOD ─────────────────────────────────────────────────
  {
    wardId: 'homewood',
    wardName: 'Homewood',
    cityId: 'pittsburgh',
    catalogCount: 22,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'braddock-steel-works',
        name: 'Edgar Thomson Steel Works — Last Operating Mill',
        arabicName: 'مَصْنَعُ الصُّلْب الأَخِير',
        category: 'structure',
        wardSpecific: ['homewood'],
        zahir: {
          sensory: 'Edgar Thomson Steel Works — last operating mill in Allegheny County. Community life persisting through industry\'s decline.',
          sight: 'The steel mill, the surrounding community, the contrast between industrial operation and community decline.',
          smell: 'Steel production, industrial air, the smell of a working mill.',
          texture: 'Industrial infrastructure, community buildings.',
          sound: 'Mill operations, community voices, the sounds of industrial persistence.',
          huduthDhati: 'A steel mill as community anchor — its Huduth Dhati is both the physical (the mill, the jobs) and the social (the community organized around it).',
          huduthZamani: 'The Edgar Thomson Steel Works has operated since 1875. Its persistence while other mills closed is a Huduth Zamani of industrial survival.'
        },
        sunnatProcesses: [
          {
            processName: 'Industrial Legacy',
            namesOfAllah: [
              { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'The Life-Giver — community life persisting through industry\'s decline' },
              { arabic: 'الْخَالِق', transliteration: 'Al-Khaliq', meaning: 'The Creator — new community forms emerging from industrial legacy' }
            ],
            description: 'Community life persisting through industry\'s decline is Al-Muhyi manifest — life finding new forms when old structures fall.',
          },
          {
            processName: 'Steel Ecology',
            namesOfAllah: [
              { arabic: 'الشَّهِيد', transliteration: 'Al-Shahid', meaning: 'The Witness — industrial history as community testimony' }
            ],
            description: 'The steel industry\'s legacy is written into the community\'s ecology — Al-Shahid in the landscape that bears witness to industrial history.',
          }
        ],
        haqq: {
          zahirStatement: 'Edgar Thomson Steel Works in Braddock, near Pittsburgh.',
          batinStatement: 'Al-Muhyi manifest in community life persisting through industrial decline — and Al-Khaliq expressed in new community forms emerging from the industrial legacy.',
          ayat: [
            { ref: '13:11', arabic: 'إِنَّ اللَّهَ لَا يُغَيِّرُ مَا بِقَوْمٍ حَتَّىٰ يُغَيِّرُوا مَا بِأَنفُسِهِمْ', translation: 'Indeed, Allah will not change the condition of a people until they change what is in themselves.' }
          ]
        },
        amanah: {
          statement: 'Support Braddock community development initiatives. Connect to industrial legacy and community transformation resources.',
          namesActive: [
            { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'Community persistence as life-giving' },
            { arabic: 'الْخَالِق', transliteration: 'Al-Khaliq', meaning: 'New community forms as creation' }
          ],
          reshatReference: 'The Caretaker who sees Braddock\'s transformation as Al-Muhyi wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Income', 'Justice']
      }
    ]
  },
  // ── PITTSBURGH — HAZELWOOD ────────────────────────────────────────────────
  {
    wardId: 'hazelwood',
    wardName: 'Hazelwood',
    cityId: 'pittsburgh',
    catalogCount: 18,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'hazelwood-brownfield',
        name: 'Hazelwood Green — Brownfield Justice',
        arabicName: 'عَدَالَةُ الأَرَاضِي الْمُلَوَّثَة',
        category: 'structure',
        wardSpecific: ['hazelwood'],
        zahir: {
          sensory: 'Former steel site being redeveloped — Al-Haqq asks: whose green? Al-Adl measures who benefits.',
          sight: 'Former industrial site, new development, the surrounding community.',
          smell: 'Industrial residue, new construction.',
          texture: 'Former industrial land, new construction.',
          sound: 'Construction, community voices.',
          huduthDhati: 'A brownfield redevelopment — its Huduth Dhati is both the physical (the land, the contamination) and the political (who benefits from the redevelopment).',
          huduthZamani: 'The Hazelwood steel site closed in the 1990s. Its redevelopment as a tech campus is a Huduth Zamani of contested urban transformation.'
        },
        sunnatProcesses: [
          {
            processName: 'Brownfield Justice',
            namesOfAllah: [
              { arabic: 'الْحَق', transliteration: 'Al-Haqq', meaning: 'The Truth — whose green? who benefits?' },
              { arabic: 'الْعَدْل', transliteration: 'Al-Adl', meaning: 'The Just — measuring who benefits from redevelopment' }
            ],
            description: 'Brownfield redevelopment raises justice questions — Al-Haqq asks whose green, and Al-Adl measures who benefits.',
          },
          {
            processName: 'Land Reclamation',
            namesOfAllah: [
              { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'The Life-Giver — land reclamation as life restoration' }
            ],
            description: 'Land reclamation from industrial contamination is Al-Muhyi manifest — life restored to earth that was poisoned.',
          }
        ],
        haqq: {
          zahirStatement: 'Hazelwood Green brownfield redevelopment in Pittsburgh.',
          batinStatement: 'Al-Haqq manifest in the justice question of who benefits — and Al-Adl expressed in the measurement of equitable development.',
          ayat: [
            { ref: '4:135', arabic: 'كُونُوا قَوَّامِينَ بِالْقِسْطِ شُهَدَاءَ لِلَّهِ', translation: 'Be persistently standing firm in justice, witnesses for Allah.' }
          ]
        },
        amanah: {
          statement: 'Advocate for equitable development in Hazelwood. Connect to community benefits agreements and environmental justice resources.',
          namesActive: [
            { arabic: 'الْحَق', transliteration: 'Al-Haqq', meaning: 'Development justice as truth' },
            { arabic: 'الْعَدْل', transliteration: 'Al-Adl', meaning: 'Equitable development as justice' }
          ],
          reshatReference: 'The Caretaker who asks whose green wears a thread of Taqwah.'
        },
        resourceCategories: ['Justice', 'Community', 'Income']
      }
    ]
  },
  // ── PITTSBURGH — MANCHESTER ───────────────────────────────────────────────
  {
    wardId: 'manchester',
    wardName: 'Manchester',
    cityId: 'pittsburgh',
    catalogCount: 15,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'manchester-community-archive',
        name: 'Manchester Community Archive — Memory Preservation',
        arabicName: 'أَرْشِيفُ مَجْتَمَع مَانْشِسْتَر',
        category: 'community',
        wardSpecific: ['manchester'],
        zahir: {
          sensory: 'Historic Black community documented before and during displacement. Archives preserving what the wrecking ball attempted to erase.',
          sight: 'Archive materials, photographs, community gathering spaces.',
          smell: 'Old paper, photographs, the smell of preserved memory.',
          texture: 'Archive materials, photographs.',
          sound: 'Community voices, oral histories.',
          huduthDhati: 'A community archive as memory preservation — its Huduth Dhati is both the physical (the documents, the photographs) and the communal (the memory it preserves).',
          huduthZamani: 'Manchester\'s Black community was displaced by urban renewal. The archive preserves what was lost.'
        },
        sunnatProcesses: [
          {
            processName: 'Community Archive',
            namesOfAllah: [
              { arabic: 'الشَّهِيد', transliteration: 'Al-Shahid', meaning: 'The Witness — archive as community witness' },
              { arabic: 'الْبَصِير', transliteration: 'Al-Basir', meaning: 'The All-Seeing — photographic witness to community history' }
            ],
            description: 'The community archive bears witness to what was here — Al-Shahid in the form of preserved memory.',
          },
          {
            processName: 'Memory Preservation',
            namesOfAllah: [
              { arabic: 'الشَّهِيد', transliteration: 'Al-Shahid', meaning: 'The Witness — preserving community memory against erasure' }
            ],
            description: 'Memory preservation against erasure is Al-Shahid in action — the community that preserves its memory preserves its identity.',
          }
        ],
        haqq: {
          zahirStatement: 'Manchester community archive in Pittsburgh.',
          batinStatement: 'Al-Shahid manifest in community memory preservation — and Al-Basir expressed in photographic witness to what was here.',
          ayat: [
            { ref: '6:38', arabic: 'مَّا فَرَّطْنَا فِي الْكِتَابِ مِن شَيْءٍ', translation: 'We have not neglected in the Register a thing.' }
          ]
        },
        amanah: {
          statement: 'Support Manchester community archive and memory preservation. Connect to oral history and community documentation resources.',
          namesActive: [
            { arabic: 'الشَّهِيد', transliteration: 'Al-Shahid', meaning: 'Memory preservation as witness' },
            { arabic: 'الْبَصِير', transliteration: 'Al-Basir', meaning: 'Photographic witness as seeing' }
          ],
          reshatReference: 'The Caretaker who preserves community memory wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Education', 'Justice']
      }
    ]
  },
  // ── PITTSBURGH — LARIMER ──────────────────────────────────────────────────
  {
    wardId: 'larimer',
    wardName: 'Larimer',
    cityId: 'pittsburgh',
    catalogCount: 20,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'larimer-participatory-planning',
        name: 'Larimer Community Planning — 8 Years of Consensus',
        arabicName: 'تَخْطِيطُ مَجْتَمَع لَارِيمَر',
        category: 'community',
        wardSpecific: ['larimer'],
        zahir: {
          sensory: 'Community-led planning that took 8 years. Al-Jami gathered residents while As-Sabur sustained them.',
          sight: 'Community meetings, planning documents, neighborhood improvements.',
          smell: 'Community spaces, the smell of a neighborhood planning its future.',
          texture: 'The texture of a community that has built consensus over years.',
          sound: 'Community voices, planning discussions.',
          huduthDhati: 'A community planning process — its Huduth Dhati is both the physical (the plans, the improvements) and the social (the consensus that was built).',
          huduthZamani: 'Larimer\'s community planning process took 8 years. Its patience is a Huduth Zamani of As-Sabur in community governance.'
        },
        sunnatProcesses: [
          {
            processName: 'Participatory Design',
            namesOfAllah: [
              { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'The Gatherer — community gathering for planning' },
              { arabic: 'الصَّابِر', transliteration: 'As-Sabur', meaning: 'The Patient — 8 years of patient consensus-building' }
            ],
            description: 'Community-led planning is Al-Jami manifest — gathering residents to shape their own future.',
          },
          {
            processName: 'Patient Governance',
            namesOfAllah: [
              { arabic: 'الْحَكِيم', transliteration: 'Al-Hakim', meaning: 'The Wise — wisdom in community governance' }
            ],
            description: 'Patient community governance is Al-Hakim manifest — wisdom expressed through inclusive decision-making.',
          }
        ],
        haqq: {
          zahirStatement: 'Larimer community planning process in Pittsburgh.',
          batinStatement: 'Al-Jami manifest in community gathering — and As-Sabur expressed in 8 years of patient consensus-building.',
          ayat: [
            { ref: '3:159', arabic: 'وَشَاوِرْهُمْ فِي الْأَمْرِ', translation: 'And consult them in the matter.' }
          ]
        },
        amanah: {
          statement: 'Support Larimer community planning and participatory governance. Connect to community development and civic engagement resources.',
          namesActive: [
            { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'Community gathering as Amanah' },
            { arabic: 'الصَّابِر', transliteration: 'As-Sabur', meaning: 'Patient governance as patience' }
          ],
          reshatReference: 'The Caretaker who practices patient consensus-building wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Justice', 'Education']
      }
    ]
  },
  // ── PITTSBURGH — STRIP DISTRICT ───────────────────────────────────────────
  {
    wardId: 'strip-district',
    wardName: 'Strip District',
    cityId: 'pittsburgh',
    catalogCount: 27,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'strip-district-food-hub',
        name: 'Strip District Food Hub — Halal Supply Chain',
        arabicName: 'مَرْكَزُ الطَّعَام فِي سْتْرِيب',
        category: 'community',
        wardSpecific: ['strip-district'],
        zahir: {
          sensory: 'Pittsburgh\'s food hub — halal butchers, produce vendors, specialty grocers provisioning across cultural lines.',
          sight: 'Market stalls, halal butchers, produce vendors.',
          smell: 'Fresh produce, halal meat, spices.',
          texture: 'Market textures, fresh food.',
          sound: 'Market voices, vendors calling.',
          huduthDhati: 'A food hub as provision center — its Huduth Dhati is both the physical (the market, the vendors) and the social (the provision networks it creates).',
          huduthZamani: 'The Strip District has been Pittsburgh\'s food hub for over a century.'
        },
        sunnatProcesses: [
          {
            processName: 'Market Ecology',
            namesOfAllah: [
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — market as provision ecology' },
              { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'The Gatherer — market gathering diverse provision' }
            ],
            description: 'The food market is Ar-Razzaq manifest — provision flowing through human hands at the confluence of rivers.',
          },
          {
            processName: 'Halal Supply Chain',
            namesOfAllah: [
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — halal provision as divine provision' }
            ],
            description: 'The halal supply chain is Ar-Razzaq flowing through Islamic provision networks — divine provision made accessible.',
          }
        ],
        haqq: {
          zahirStatement: 'Strip District food hub in Pittsburgh.',
          batinStatement: 'Ar-Razzaq manifest in market provision — and Al-Jami expressed in the gathering of diverse provision at the confluence.',
          ayat: [
            { ref: '2:172', arabic: 'كُلُوا مِن طَيِّبَاتِ مَا رَزَقْنَاكُمْ', translation: 'Eat of the good things We have provided for you.' }
          ]
        },
        amanah: {
          statement: 'Support Strip District vendors and halal provision networks. Connect to food access and cultural provision resources.',
          namesActive: [
            { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'Market provision as Amanah' },
            { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'Diverse gathering as community' }
          ],
          reshatReference: 'The Caretaker who supports halal provision networks wears a thread of Taqwah.'
        },
        resourceCategories: ['Food', 'Community', 'Income']
      }
    ]
  },
  // ── PITTSBURGH — GARFIELD ─────────────────────────────────────────────────
  {
    wardId: 'garfield',
    wardName: 'Garfield',
    cityId: 'pittsburgh',
    catalogCount: 16,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'wilkinsburg-library',
        name: 'Wilkinsburg Library — Knowledge Persistence',
        arabicName: 'مَكْتَبَةُ وِيلْكِنْزْبَرْغ',
        category: 'community',
        wardSpecific: ['garfield'],
        zahir: {
          sensory: 'Library and learning centers persisting despite municipal financial crisis. Knowledge access maintained despite austerity.',
          sight: 'The library building, community members accessing knowledge.',
          smell: 'Books, the smell of a library persisting through crisis.',
          texture: 'Books, library materials.',
          sound: 'Community voices, the sounds of a library in use.',
          huduthDhati: 'A library as knowledge anchor — its Huduth Dhati is both the physical (the building, the books) and the social (the knowledge access it provides).',
          huduthZamani: 'Wilkinsburg has faced municipal financial crisis for decades. The library\'s persistence is a Huduth Zamani of Al-Nur in community knowledge.'
        },
        sunnatProcesses: [
          {
            processName: 'Knowledge Persistence',
            namesOfAllah: [
              { arabic: 'النُّور', transliteration: 'Al-Nur', meaning: 'The Light — library as light of knowledge' },
              { arabic: 'الْعَلِيم', transliteration: 'Al-Alim', meaning: 'The All-Knowing — knowledge access as divine attribute' }
            ],
            description: 'The library persisting through crisis is Al-Nur manifest — light of knowledge maintained despite austerity.',
          }
        ],
        haqq: {
          zahirStatement: 'Wilkinsburg library persisting through municipal financial crisis.',
          batinStatement: 'Al-Nur manifest in knowledge persistence — and Al-Alim expressed in community learning access maintained despite austerity.',
          ayat: [
            { ref: '96:1', arabic: 'اقْرَأْ بِاسْمِ رَبِّكَ الَّذِي خَلَقَ', translation: 'Read in the name of your Lord who created.' }
          ]
        },
        amanah: {
          statement: 'Support Wilkinsburg library and community learning centers. Connect to knowledge access and educational equity resources.',
          namesActive: [
            { arabic: 'النُّور', transliteration: 'Al-Nur', meaning: 'Knowledge light as Amanah' },
            { arabic: 'الْعَلِيم', transliteration: 'Al-Alim', meaning: 'Community learning as divine knowledge' }
          ],
          reshatReference: 'The Caretaker who maintains knowledge access wears a thread of Taqwah.'
        },
        resourceCategories: ['Education', 'Community', 'Justice']
      }
    ]
  },
  // ── DURHAM — HAYTI ────────────────────────────────────────────────────────
  {
    wardId: 'hayti',
    wardName: 'Hayti',
    cityId: 'durham',
    catalogCount: 29,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'hayti-heritage-center',
        name: 'Hayti Heritage Center — Black Wall Street Memory',
        arabicName: 'مَرْكَزُ تُرَاثِ هَايْتِي',
        category: 'community',
        wardSpecific: ['hayti'],
        zahir: {
          sensory: 'Hayti — once the Black Wall Street of the South, now a memory held by those who remember. The Heritage Center preserving what urban renewal attempted to erase.',
          sight: 'The Heritage Center, community gathering spaces.',
          smell: 'The smell of a community preserving its history.',
          texture: 'Archive materials, community spaces.',
          sound: 'Community voices, oral histories.',
          huduthDhati: 'A heritage center as community memory — its Huduth Dhati is both the physical (the building, the archives) and the communal (the memory it preserves).',
          huduthZamani: 'Hayti was devastated by urban renewal in the 1960s. The Heritage Center preserves what was lost.'
        },
        sunnatProcesses: [
          {
            processName: 'Knowledge Endurance',
            namesOfAllah: [
              { arabic: 'الْبَاقِي', transliteration: 'Al-Baqi', meaning: 'The Everlasting — community memory enduring through displacement' },
              { arabic: 'الشَّهِيد', transliteration: 'Al-Shahid', meaning: 'The Witness — heritage center as community witness' }
            ],
            description: 'Community memory enduring through displacement is Al-Baqi manifest — what was here persists in the memory of those who carry it.',
          },
          {
            processName: 'HBCU Ecology',
            namesOfAllah: [
              { arabic: 'النُّور', transliteration: 'Al-Nur', meaning: 'The Light — HBCU as light of knowledge' }
            ],
            description: 'HBCU institutions in Durham are Al-Nur manifest — light of knowledge finding alternative pathways through obstruction.',
          }
        ],
        haqq: {
          zahirStatement: 'Hayti Heritage Center in Durham.',
          batinStatement: 'Al-Baqi manifest in community memory enduring through displacement — and Al-Shahid expressed in the heritage center bearing witness to what was here.',
          ayat: [
            { ref: '6:38', arabic: 'مَّا فَرَّطْنَا فِي الْكِتَابِ مِن شَيْءٍ', translation: 'We have not neglected in the Register a thing.' }
          ]
        },
        amanah: {
          statement: 'Support Hayti Heritage Center and community memory preservation. Connect to cultural heritage and community development resources.',
          namesActive: [
            { arabic: 'الْبَاقِي', transliteration: 'Al-Baqi', meaning: 'Memory endurance as Amanah' },
            { arabic: 'الشَّهِيد', transliteration: 'Al-Shahid', meaning: 'Heritage witness as testimony' }
          ],
          reshatReference: 'The Caretaker who preserves Hayti\'s memory wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Education', 'Justice']
      }
    ]
  },
  // ── DURHAM — NCCU CAMPUS ──────────────────────────────────────────────────
  {
    wardId: 'nccu-campus',
    wardName: 'NCCU Campus / East Durham',
    cityId: 'durham',
    catalogCount: 22,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'east-durham-mutual-aid',
        name: 'East Durham Mutual Aid — Immigrant Provision Networks',
        arabicName: 'شَبَكَاتُ التَّعَاوُن فِي دَرْهَام',
        category: 'community',
        wardSpecific: ['nccu-campus'],
        zahir: {
          sensory: 'Mutual aid networks provisioning immigrant and refugee families through shared food systems. Community fridges, food sharing, multilingual care across cultural lines.',
          sight: 'Community fridges, food sharing spaces, multilingual signage.',
          smell: 'Diverse foods, community kitchens.',
          texture: 'Community food, shared spaces.',
          sound: 'Multiple languages, community voices.',
          huduthDhati: 'A mutual aid network as provision system — its Huduth Dhati is both the physical (the food, the fridges) and the social (the networks of care).',
          huduthZamani: 'East Durham\'s mutual aid networks emerged from immigrant and refugee communities.'
        },
        sunnatProcesses: [
          {
            processName: 'Mutual Provision',
            namesOfAllah: [
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — mutual aid as divine provision' },
              { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'The Gatherer — community gathering for provision' }
            ],
            description: 'Mutual aid provision is Ar-Razzaq manifest — divine provision flowing through community hands.',
          },
          {
            processName: 'Multilingual Care',
            namesOfAllah: [
              { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'The Gatherer — multilingual care gathering across cultural lines' }
            ],
            description: 'Multilingual care across cultural lines is Al-Jami manifest — gathering what was scattered into community.',
          }
        ],
        haqq: {
          zahirStatement: 'East Durham mutual aid networks.',
          batinStatement: 'Ar-Razzaq manifest in mutual provision — and Al-Jami expressed in multilingual care gathering across cultural lines.',
          ayat: [
            { ref: '2:177', arabic: 'وَآتَى الْمَالَ عَلَىٰ حُبِّهِ ذَوِي الْقُرْبَىٰ وَالْيَتَامَىٰ وَالْمَسَاكِينَ', translation: 'And gives wealth, in spite of love for it, to relatives, orphans, the needy.' }
          ]
        },
        amanah: {
          statement: 'Support East Durham mutual aid networks. Connect to immigrant and refugee provision resources.',
          namesActive: [
            { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'Mutual provision as Amanah' },
            { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'Multilingual gathering as community' }
          ],
          reshatReference: 'The Caretaker who supports mutual aid wears a thread of Taqwah.'
        },
        resourceCategories: ['Food', 'Community', 'Justice']
      }
    ]
  },
  // ── DURHAM — WALLTOWN ─────────────────────────────────────────────────────
  {
    wardId: 'walltown',
    wardName: 'Walltown',
    cityId: 'durham',
    catalogCount: 18,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'walltown-homeowner-resistance',
        name: 'Walltown Homeowners — Resisting Duke Expansion',
        arabicName: 'مُقَاوَمَةُ أَصْحَابِ الْمَنَازِل',
        category: 'community',
        wardSpecific: ['walltown'],
        zahir: {
          sensory: 'Homeowners resisting Duke University expansion for decades — property deed as resistance language.',
          sight: 'Homeowner properties, Duke University expansion.',
          smell: 'The smell of a community holding its ground.',
          texture: 'Property deeds, community spaces.',
          sound: 'Community voices, advocacy.',
          huduthDhati: 'A homeowner community as resistance — its Huduth Dhati is both the physical (the properties, the deeds) and the social (the organized resistance).',
          huduthZamani: 'Walltown homeowners have resisted Duke University expansion for decades.'
        },
        sunnatProcesses: [
          {
            processName: 'Land Resistance',
            namesOfAllah: [
              { arabic: 'الصَّابِر', transliteration: 'As-Sabur', meaning: 'The Patient — decades of patient resistance' },
              { arabic: 'الْحَق', transliteration: 'Al-Haqq', meaning: 'The Truth — property rights as truth' }
            ],
            description: 'Homeowner resistance to institutional pressure is As-Sabur manifest — patient persistence in the face of power.',
          }
        ],
        haqq: {
          zahirStatement: 'Walltown homeowner resistance to Duke University expansion.',
          batinStatement: 'As-Sabur manifest in patient resistance — and Al-Haqq expressed in property rights as community truth.',
          ayat: [
            { ref: '30:60', arabic: 'فَاصْبِرْ إِنَّ وَعْدَ اللَّهِ حَقٌّ', translation: 'So be patient. Indeed, the promise of Allah is truth.' }
          ]
        },
        amanah: {
          statement: 'Support Walltown homeowners and community land rights. Connect to housing justice and community development resources.',
          namesActive: [
            { arabic: 'الصَّابِر', transliteration: 'As-Sabur', meaning: 'Patient resistance as Amanah' },
            { arabic: 'الْحَق', transliteration: 'Al-Haqq', meaning: 'Property rights as truth' }
          ],
          reshatReference: 'The Caretaker who supports homeowner resistance wears a thread of Taqwah.'
        },
        resourceCategories: ['Housing', 'Justice', 'Community']
      }
    ]
  },
  // ── DURHAM — OLD NORTH DURHAM ─────────────────────────────────────────────
  {
    wardId: 'old-north-durham',
    wardName: 'Old North Durham',
    cityId: 'durham',
    catalogCount: 16,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'old-north-durham-masjid-garden',
        name: 'Masjid Garden — Sacred Agriculture',
        arabicName: 'حَدِيقَةُ الْمَسْجِد',
        category: 'plant',
        wardSpecific: ['old-north-durham'],
        zahir: {
          sensory: 'Community gardens adjacent to masajid growing medicinal herbs alongside vegetables. Sacred agriculture connecting spiritual practice to ecological stewardship.',
          sight: 'Garden beds, medicinal herbs, vegetables, the masjid.',
          smell: 'Medicinal herbs, growing vegetables.',
          texture: 'Garden soil, growing plants.',
          sound: 'Community voices, growing things.',
          huduthDhati: 'A masjid garden as sacred agriculture — its Huduth Dhati is both the physical (the garden, the plants) and the spiritual (the connection to Islamic ecological practice).',
          huduthZamani: 'Masjid gardens have a long history in Islamic tradition. Their revival in Durham is a Huduth Zamani of sacred agriculture returning.'
        },
        sunnatProcesses: [
          {
            processName: 'Masjid Garden',
            namesOfAllah: [
              { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'The Life-Giver — garden as life-giving practice' },
              { arabic: 'الْخَالِق', transliteration: 'Al-Khaliq', meaning: 'The Creator — sacred agriculture as creation participation' }
            ],
            description: 'The masjid garden is Al-Muhyi manifest — life-giving practice connecting spiritual and ecological stewardship.',
          }
        ],
        haqq: {
          zahirStatement: 'Masjid garden in Old North Durham.',
          batinStatement: 'Al-Muhyi manifest in sacred agriculture — and Al-Khaliq expressed in the garden as participation in creation.',
          ayat: [
            { ref: '6:99', arabic: 'وَهُوَ الَّذِي أَنزَلَ مِنَ السَّمَاءِ مَاءً فَأَخْرَجْنَا بِهِ نَبَاتَ كُلِّ شَيْءٍ', translation: 'And it is He who sends down rain from the sky, and We produce thereby the growth of all things.' }
          ]
        },
        amanah: {
          statement: 'Support masjid gardens and sacred agriculture in Durham. Connect to Islamic ecological practice and community garden resources.',
          namesActive: [
            { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'Sacred agriculture as life-giving' },
            { arabic: 'الْخَالِق', transliteration: 'Al-Khaliq', meaning: 'Garden as creation participation' }
          ],
          reshatReference: 'The Caretaker who tends the masjid garden wears a thread of Taqwah.'
        },
        resourceCategories: ['Food', 'Health', 'Community']
      }
    ]
  },
  // ── DURHAM — SOUTHSIDE ────────────────────────────────────────────────────
  {
    wardId: 'southside',
    wardName: 'Southside',
    cityId: 'durham',
    catalogCount: 14,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'crest-street-env-justice',
        name: 'Crest Street — First Environmental Justice Victory',
        arabicName: 'انْتِصَارُ الْعَدَالَة الْبِيئِيَّة',
        category: 'community',
        wardSpecific: ['southside'],
        zahir: {
          sensory: 'Community that successfully fought highway expansion in the 1970s — one of the first environmental justice victories.',
          sight: 'The neighborhood, the highway that wasn\'t built.',
          smell: 'The smell of a community that has won a long fight.',
          texture: 'Community spaces.',
          sound: 'Community voices.',
          huduthDhati: 'A community victory as environmental justice — its Huduth Dhati is both the physical (the neighborhood, the highway not built) and the social (the organizing that won).',
          huduthZamani: 'Crest Street fought highway expansion in the 1970s and won.'
        },
        sunnatProcesses: [
          {
            processName: 'Environmental Justice',
            namesOfAllah: [
              { arabic: 'الشَّهِيد', transliteration: 'Al-Shahid', meaning: 'The Witness — community victory as testimony' },
              { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'The Gatherer — community organizing as gathering' }
            ],
            description: 'The environmental justice victory is Al-Shahid manifest — the community bearing witness to its own power.',
          }
        ],
        haqq: {
          zahirStatement: 'Crest Street environmental justice victory in Durham.',
          batinStatement: 'Al-Shahid manifest in community victory — and Al-Jami expressed in the organizing that gathered community power.',
          ayat: [
            { ref: '16:90', arabic: 'إِنَّ اللَّهَ يَأْمُرُ بِالْعَدْلِ وَالْإِحْسَانِ', translation: 'Indeed, Allah commands justice and good conduct.' }
          ]
        },
        amanah: {
          statement: 'Support environmental justice organizing in Durham. Connect to community organizing and environmental justice resources.',
          namesActive: [
            { arabic: 'الشَّهِيد', transliteration: 'Al-Shahid', meaning: 'Community victory as witness' },
            { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'Organizing as gathering' }
          ],
          reshatReference: 'The Caretaker who organizes for environmental justice wears a thread of Taqwah.'
        },
        resourceCategories: ['Justice', 'Community', 'Health']
      }
    ]
  },
  // ── LOUISVILLE — WEST LOUISVILLE ──────────────────────────────────────────
  {
    wardId: 'west-louisville',
    wardName: 'West Louisville',
    cityId: 'louisville',
    catalogCount: 38,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'west-louisville-refugee-provision',
        name: 'West Louisville Refugee Provision Networks',
        arabicName: 'شَبَكَاتُ رِزْق اللَّاجِئِين',
        category: 'community',
        wardSpecific: ['west-louisville'],
        zahir: {
          sensory: 'Refugee communities — Somali, Congolese, Bhutanese — building provision networks from scratch. Kitchen co-ops, cultural food sovereignty.',
          sight: 'Refugee community spaces, cultural food markets.',
          smell: 'Diverse cultural foods, community kitchens.',
          texture: 'Community food, shared spaces.',
          sound: 'Multiple languages, community voices.',
          huduthDhati: 'A refugee provision network — its Huduth Dhati is both the physical (the food, the kitchens) and the social (the networks of care built from scratch).',
          huduthZamani: 'Louisville has received refugee communities for decades.'
        },
        sunnatProcesses: [
          {
            processName: 'Refugee Rizq',
            namesOfAllah: [
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — refugee provision as divine Rizq' },
              { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'The Gatherer — diverse communities gathering for provision' }
            ],
            description: 'Refugee provision networks are Ar-Razzaq manifest — divine provision flowing through diaspora hands.',
          }
        ],
        haqq: {
          zahirStatement: 'Refugee provision networks in West Louisville.',
          batinStatement: 'Ar-Razzaq manifest in refugee provision — and Al-Jami expressed in diverse communities gathering for food sovereignty.',
          ayat: [
            { ref: '2:172', arabic: 'كُلُوا مِن طَيِّبَاتِ مَا رَزَقْنَاكُمْ', translation: 'Eat of the good things We have provided for you.' }
          ]
        },
        amanah: {
          statement: 'Support refugee provision networks in West Louisville. Connect to refugee resettlement and food sovereignty resources.',
          namesActive: [
            { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'Refugee provision as Amanah' },
            { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'Diverse gathering as community' }
          ],
          reshatReference: 'The Caretaker who supports refugee provision wears a thread of Taqwah.'
        },
        resourceCategories: ['Food', 'Community', 'Justice']
      }
    ]
  },
  // ── LOUISVILLE — SMOKETOWN ────────────────────────────────────────────────
  {
    wardId: 'smoketown',
    wardName: 'Smoketown',
    cityId: 'louisville',
    catalogCount: 19,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'smoketown-second-chance-education',
        name: 'Smoketown Second Chance Education Centers',
        arabicName: 'مَرَاكِزُ التَّعْلِيم الثَّانِي',
        category: 'community',
        wardSpecific: ['smoketown'],
        zahir: {
          sensory: 'Community centers offering tutoring, GED preparation, digital literacy — storefront as school.',
          sight: 'Community learning centers, tutoring spaces.',
          smell: 'The smell of a community learning space.',
          texture: 'Learning materials, community spaces.',
          sound: 'Learning voices, tutoring sessions.',
          huduthDhati: 'A community learning center as second chance education — its Huduth Dhati is both the physical (the space, the materials) and the social (the knowledge access it provides).',
          huduthZamani: 'Smoketown\'s community learning centers emerged from the failure of formal education systems.'
        },
        sunnatProcesses: [
          {
            processName: 'Second Chance Education',
            namesOfAllah: [
              { arabic: 'الْعَلِيم', transliteration: 'Al-Alim', meaning: 'The All-Knowing — knowledge access as divine attribute' },
              { arabic: 'النُّور', transliteration: 'Al-Nur', meaning: 'The Light — education as light in darkness' }
            ],
            description: 'Second chance education is Al-Alim\'s Amanah — knowledge access provided where formal systems have failed.',
          }
        ],
        haqq: {
          zahirStatement: 'Smoketown second chance education centers in Louisville.',
          batinStatement: 'Al-Alim manifest in knowledge access — and Al-Nur expressed in education as light finding alternative pathways.',
          ayat: [
            { ref: '96:1', arabic: 'اقْرَأْ بِاسْمِ رَبِّكَ الَّذِي خَلَقَ', translation: 'Read in the name of your Lord who created.' }
          ]
        },
        amanah: {
          statement: 'Support Smoketown community learning centers. Connect to second chance education and digital literacy resources.',
          namesActive: [
            { arabic: 'الْعَلِيم', transliteration: 'Al-Alim', meaning: 'Knowledge access as Amanah' },
            { arabic: 'النُّور', transliteration: 'Al-Nur', meaning: 'Education as light' }
          ],
          reshatReference: 'The Caretaker who supports second chance education wears a thread of Taqwah.'
        },
        resourceCategories: ['Education', 'Community', 'Income']
      }
    ]
  },
  // ── LOUISVILLE — RUSSELL ──────────────────────────────────────────────────
  {
    wardId: 'russell',
    wardName: 'Russell',
    cityId: 'louisville',
    catalogCount: 24,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'russell-homeowner-persistence',
        name: 'Russell Homeowners — Stubborn Maintenance',
        arabicName: 'صَبْرُ أَصْحَابِ الْمَنَازِل',
        category: 'community',
        wardSpecific: ['russell'],
        zahir: {
          sensory: 'Homeowners maintaining properties through systematic devaluation — every repaired porch is As-Sabur.',
          sight: 'Maintained homes, repaired porches.',
          smell: 'Paint, wood, the smell of a maintained home.',
          texture: 'Repaired wood, maintained surfaces.',
          sound: 'Community voices, maintenance work.',
          huduthDhati: 'A homeowner community as persistence — its Huduth Dhati is both the physical (the homes, the repairs) and the social (the community that maintains through devaluation).',
          huduthZamani: 'Russell has faced systematic devaluation for decades.'
        },
        sunnatProcesses: [
          {
            processName: 'Homeowner Patience',
            namesOfAllah: [
              { arabic: 'الصَّابِر', transliteration: 'As-Sabur', meaning: 'The Patient — homeowner persistence through devaluation' },
              { arabic: 'الْبَاقِي', transliteration: 'Al-Baqi', meaning: 'The Everlasting — community persistence through systematic devaluation' }
            ],
            description: 'Homeowner maintenance through systematic devaluation is As-Sabur manifest — patient persistence in the face of structural disinvestment.',
          }
        ],
        haqq: {
          zahirStatement: 'Russell homeowners maintaining through systematic devaluation.',
          batinStatement: 'As-Sabur manifest in homeowner persistence — and Al-Baqi expressed in community enduring through systematic devaluation.',
          ayat: [
            { ref: '30:60', arabic: 'فَاصْبِرْ إِنَّ وَعْدَ اللَّهِ حَقٌّ', translation: 'So be patient. Indeed, the promise of Allah is truth.' }
          ]
        },
        amanah: {
          statement: 'Support Russell homeowners and community property stewardship. Connect to housing justice and community development resources.',
          namesActive: [
            { arabic: 'الصَّابِر', transliteration: 'As-Sabur', meaning: 'Homeowner persistence as Amanah' },
            { arabic: 'الْبَاقِي', transliteration: 'Al-Baqi', meaning: 'Community endurance as everlasting' }
          ],
          reshatReference: 'The Caretaker who maintains their home through devaluation wears a thread of Taqwah.'
        },
        resourceCategories: ['Housing', 'Community', 'Justice']
      }
    ]
  },
  // ── LOUISVILLE — NEWBURG ──────────────────────────────────────────────────
  {
    wardId: 'newburg',
    wardName: 'Newburg',
    cityId: 'louisville',
    catalogCount: 15,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'newburg-creek-restoration',
        name: 'Newburg Creek Restoration — Watershed Classroom',
        arabicName: 'اسْتِعَادَةُ مَجْرَى الْمَاء',
        category: 'water',
        wardSpecific: ['newburg'],
        zahir: {
          sensory: 'Creek restoration teaching watershed ecology through Islamic creation lens — drainage as divine engineering.',
          sight: 'Restored creek, watershed ecology, community learning.',
          smell: 'Creek water, restored vegetation.',
          texture: 'Creek banks, restored vegetation.',
          sound: 'Creek flow, community voices.',
          huduthDhati: 'A creek restoration as watershed classroom — its Huduth Dhati is both the physical (the creek, the restoration) and the educational (the ecological knowledge it teaches).',
          huduthZamani: 'Newburg\'s creek was degraded by development. Its restoration is a Huduth Zamani of Al-Muhyi in watershed healing.'
        },
        sunnatProcesses: [
          {
            processName: 'Creek Restoration',
            namesOfAllah: [
              { arabic: 'الْخَالِق', transliteration: 'Al-Khaliq', meaning: 'The Creator — creek restoration as creation participation' },
              { arabic: 'الْعَلِيم', transliteration: 'Al-Alim', meaning: 'The All-Knowing — watershed knowledge as divine knowledge' }
            ],
            description: 'Creek restoration is Al-Khaliq\'s Amanah — the Caretaker who restores the watershed participates in creation.',
          }
        ],
        haqq: {
          zahirStatement: 'Newburg creek restoration in Louisville.',
          batinStatement: 'Al-Khaliq manifest in creek restoration — and Al-Alim expressed in watershed ecology as divine knowledge.',
          ayat: [
            { ref: '21:30', arabic: 'وَجَعَلْنَا مِنَ الْمَاءِ كُلَّ شَيْءٍ حَيٍّ', translation: 'And We made from water every living thing.' }
          ]
        },
        amanah: {
          statement: 'Support Newburg creek restoration and watershed education. Connect to ecological restoration and environmental education resources.',
          namesActive: [
            { arabic: 'الْخَالِق', transliteration: 'Al-Khaliq', meaning: 'Creek restoration as creation' },
            { arabic: 'الْعَلِيم', transliteration: 'Al-Alim', meaning: 'Watershed knowledge as divine knowledge' }
          ],
          reshatReference: 'The Caretaker who restores the creek wears a thread of Taqwah.'
        },
        resourceCategories: ['Health', 'Education', 'Community']
      }
    ]
  },
  // ── PORTLAND — ALBERTA ARTS ───────────────────────────────────────────────
  {
    wardId: 'alberta-arts',
    wardName: 'Alberta Arts District',
    cityId: 'portland',
    catalogCount: 26,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'alberta-82nd-provision',
        name: '82nd Avenue — International Provision Corridor',
        arabicName: 'مَمَرُّ الرِّزْق الدَّوْلِيّ',
        category: 'community',
        wardSpecific: ['alberta-arts'],
        zahir: {
          sensory: 'Portland\'s most diverse corridor — Vietnamese, Somali, Mexican, Russian grocers creating global provision network.',
          sight: 'Diverse storefronts, international grocers.',
          smell: 'Diverse cultural foods, international spices.',
          texture: 'Market textures, diverse foods.',
          sound: 'Multiple languages, market voices.',
          huduthDhati: 'An international provision corridor — its Huduth Dhati is both the physical (the stores, the food) and the social (the diverse community it provisions).',
          huduthZamani: 'The 82nd Avenue corridor has been Portland\'s immigrant commercial hub for decades.'
        },
        sunnatProcesses: [
          {
            processName: 'International Provision',
            namesOfAllah: [
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — international provision as divine Rizq' },
              { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'The Gatherer — diverse provision gathering community' }
            ],
            description: 'The international provision corridor is Ar-Razzaq manifest — divine provision flowing through diverse immigrant hands.',
          }
        ],
        haqq: {
          zahirStatement: '82nd Avenue international provision corridor in Portland.',
          batinStatement: 'Ar-Razzaq manifest in international provision — and Al-Jami expressed in diverse cultures gathering for community.',
          ayat: [
            { ref: '49:13', arabic: 'يَا أَيُّهَا النَّاسُ إِنَّا خَلَقْنَاكُم مِّن ذَكَرٍ وَأُنثَىٰ وَجَعَلْنَاكُمْ شُعُوبًا وَقَبَائِلَ لِتَعَارَفُوا', translation: 'O mankind, indeed We have created you from male and female and made you peoples and tribes that you may know one another.' }
          ]
        },
        amanah: {
          statement: 'Support 82nd Avenue immigrant businesses and provision networks. Connect to immigrant commerce and food sovereignty resources.',
          namesActive: [
            { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'International provision as Amanah' },
            { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'Diverse gathering as community' }
          ],
          reshatReference: 'The Caretaker who supports immigrant provision wears a thread of Taqwah.'
        },
        resourceCategories: ['Food', 'Community', 'Income']
      }
    ]
  },
  // ── PORTLAND — CULLY ──────────────────────────────────────────────────────
  {
    wardId: 'cully',
    wardName: 'Cully',
    cityId: 'portland',
    catalogCount: 22,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'cully-land-trust',
        name: 'Cully Community Land Trust — Permanent Affordability',
        arabicName: 'صُنْدُوقُ الأَرَاضِي الْمُجْتَمَعِيَّة',
        category: 'community',
        wardSpecific: ['cully'],
        zahir: {
          sensory: 'Community land trust resisting displacement of Portland\'s most diverse neighborhood — slow work of land acquisition.',
          sight: 'Community land trust properties, diverse neighborhood.',
          smell: 'The smell of a community working to stay.',
          texture: 'Community properties, diverse neighborhood.',
          sound: 'Community voices, organizing.',
          huduthDhati: 'A community land trust as displacement resistance — its Huduth Dhati is both the physical (the land, the properties) and the social (the organized resistance to displacement).',
          huduthZamani: 'Cully\'s community land trust has been acquiring land for years.'
        },
        sunnatProcesses: [
          {
            processName: 'Land Trust Ecology',
            namesOfAllah: [
              { arabic: 'الصَّابِر', transliteration: 'As-Sabur', meaning: 'The Patient — slow work of land acquisition' },
              { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'The Gatherer — multicultural coalition gathering for permanence' }
            ],
            description: 'The community land trust is As-Sabur manifest — patient work of land acquisition that creates permanent affordability.',
          }
        ],
        haqq: {
          zahirStatement: 'Cully community land trust in Portland.',
          batinStatement: 'As-Sabur manifest in patient land acquisition — and Al-Jami expressed in multicultural coalition gathering for permanence.',
          ayat: [
            { ref: '30:60', arabic: 'فَاصْبِرْ إِنَّ وَعْدَ اللَّهِ حَقٌّ', translation: 'So be patient. Indeed, the promise of Allah is truth.' }
          ]
        },
        amanah: {
          statement: 'Support Cully community land trust and displacement resistance. Connect to community land trust and housing justice resources.',
          namesActive: [
            { arabic: 'الصَّابِر', transliteration: 'As-Sabur', meaning: 'Patient land acquisition as Amanah' },
            { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'Multicultural coalition as gathering' }
          ],
          reshatReference: 'The Caretaker who supports community land trusts wears a thread of Taqwah.'
        },
        resourceCategories: ['Housing', 'Community', 'Justice']
      }
    ]
  },
  // ── PORTLAND — JADE DISTRICT ──────────────────────────────────────────────
  {
    wardId: 'jade-district',
    wardName: 'Jade District / 82nd Ave',
    cityId: 'portland',
    catalogCount: 19,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'jade-district-masjid',
        name: 'Northeast Portland Masjid — Adhan Persisting',
        arabicName: 'مَسْجِدُ شَمَال شَرْق بُورْتْلَانْد',
        category: 'community',
        wardSpecific: ['jade-district'],
        zahir: {
          sensory: 'Masajid anchoring Muslim community through dramatic demographic shifts — adhan persisting across transformation.',
          sight: 'The masjid, the congregation, the surrounding neighborhood transformation.',
          smell: 'The smell of a Muslim community persisting through demographic change.',
          texture: 'The texture of an institution that has endured demographic transformation.',
          sound: 'Adhan, Quran, community voices across demographic shifts.',
          huduthDhati: 'A masjid as community anchor through demographic change — its Huduth Dhati is both the physical (the structure) and the spiritual (the community it holds).',
          huduthZamani: 'Portland\'s Muslim community has grown through immigration and demographic change.'
        },
        sunnatProcesses: [
          {
            processName: 'Masjid Endurance',
            namesOfAllah: [
              { arabic: 'الْبَاقِي', transliteration: 'Al-Baqi', meaning: 'The Everlasting — masjid persisting through demographic change' },
              { arabic: 'الصَّابِر', transliteration: 'As-Sabur', meaning: 'The Patient — patient persistence through transformation' }
            ],
            description: 'The masjid persisting through demographic change is Al-Baqi manifest — the institution that endures when everything around it transforms.',
          }
        ],
        haqq: {
          zahirStatement: 'Northeast Portland masjid persisting through demographic shifts.',
          batinStatement: 'Al-Baqi manifest in masjid endurance — and As-Sabur expressed in patient persistence through demographic transformation.',
          ayat: [
            { ref: '16:96', arabic: 'مَا عِندَكُمْ يَنفَدُ ۖ وَمَا عِندَ اللَّهِ بَاقٍ', translation: 'Whatever is with you will end, but what is with Allah endures.' }
          ]
        },
        amanah: {
          statement: 'Support Portland masajid and Muslim community institutions. Connect to Islamic community development resources.',
          namesActive: [
            { arabic: 'الْبَاقِي', transliteration: 'Al-Baqi', meaning: 'Masjid endurance as Amanah' },
            { arabic: 'الصَّابِر', transliteration: 'As-Sabur', meaning: 'Patient persistence as patience' }
          ],
          reshatReference: 'The Caretaker who supports the masjid\'s persistence wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Education', 'Health']
      }
    ]
  },
  // ── PORTLAND — ST. JOHNS ──────────────────────────────────────────────────
  {
    wardId: 'st-johns',
    wardName: 'St. Johns',
    cityId: 'portland',
    catalogCount: 17,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'st-johns-prairie-restoration',
        name: 'Cathedral Park Prairie Restoration',
        arabicName: 'اسْتِعَادَةُ السَّهْل الْعُشْبِيّ',
        category: 'plant',
        wardSpecific: ['st-johns'],
        zahir: {
          sensory: 'Cathedral Park and prairie restoration along the Willamette — ecological restoration as health.',
          sight: 'Cathedral Park, prairie restoration, the Willamette River.',
          smell: 'Prairie grasses, river air.',
          texture: 'Prairie grasses, river banks.',
          sound: 'River flow, prairie birds.',
          huduthDhati: 'A prairie restoration as ecological health — its Huduth Dhati is both the physical (the prairie, the river) and the ecological (the health it restores).',
          huduthZamani: 'St. Johns\' prairie was degraded by development. Its restoration is a Huduth Zamani of Al-Muhyi in ecological healing.'
        },
        sunnatProcesses: [
          {
            processName: 'Prairie Restoration',
            namesOfAllah: [
              { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'The Life-Giver — prairie restoration as life restoration' },
              { arabic: 'الْبَصِير', transliteration: 'Al-Basir', meaning: 'The All-Seeing — trained observation of ecological recovery' }
            ],
            description: 'Prairie restoration is Al-Muhyi manifest — life restored to land that was degraded.',
          }
        ],
        haqq: {
          zahirStatement: 'Cathedral Park prairie restoration in St. Johns, Portland.',
          batinStatement: 'Al-Muhyi manifest in prairie restoration — and Al-Basir expressed in trained ecological observation.',
          ayat: [
            { ref: '22:5', arabic: 'وَتَرَى الْأَرْضَ هَامِدَةً فَإِذَا أَنزَلْنَا عَلَيْهَا الْمَاءَ اهْتَزَّتْ وَرَبَتْ', translation: 'You see the earth barren, but when We send down rain, it quivers and grows.' }
          ]
        },
        amanah: {
          statement: 'Support St. Johns prairie restoration and river ecology. Connect to ecological restoration and environmental education resources.',
          namesActive: [
            { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'Prairie restoration as life-giving' },
            { arabic: 'الْبَصِير', transliteration: 'Al-Basir', meaning: 'Ecological observation as seeing' }
          ],
          reshatReference: 'The Caretaker who restores the prairie wears a thread of Taqwah.'
        },
        resourceCategories: ['Health', 'Community', 'Education']
      }
    ]
  },
  // ── PORTLAND — LENTS ──────────────────────────────────────────────────────
  {
    wardId: 'lents',
    wardName: 'Lents',
    cityId: 'portland',
    catalogCount: 14,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'lents-brownfield-remediation',
        name: 'Lents Brownfield Remediation — Soil Truth',
        arabicName: 'حَقِيقَةُ التُّرْبَة',
        category: 'structure',
        wardSpecific: ['lents'],
        zahir: {
          sensory: 'Brownfield remediation uncovering industrial contamination — Al-Haqq exposing hidden toxicity.',
          sight: 'Remediation equipment, contaminated soil, community testing.',
          smell: 'Industrial contamination, remediation chemicals.',
          texture: 'Contaminated soil, remediation materials.',
          sound: 'Remediation equipment, community voices.',
          huduthDhati: 'A brownfield remediation as soil truth — its Huduth Dhati is both the physical (the contamination, the remediation) and the political (who was exposed to the toxicity).',
          huduthZamani: 'Lents\' industrial contamination was hidden for decades. Its exposure through remediation is a Huduth Zamani of Al-Haqq in soil truth.'
        },
        sunnatProcesses: [
          {
            processName: 'Soil Truth',
            namesOfAllah: [
              { arabic: 'الْحَق', transliteration: 'Al-Haqq', meaning: 'The Truth — soil contamination as hidden truth exposed' },
              { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'The Life-Giver — soil remediation as life restoration' }
            ],
            description: 'Brownfield remediation is Al-Haqq manifest — the truth of industrial contamination exposed and addressed.',
          }
        ],
        haqq: {
          zahirStatement: 'Lents brownfield remediation in Portland.',
          batinStatement: 'Al-Haqq manifest in soil truth — and Al-Muhyi expressed in remediation as ecological life restoration.',
          ayat: [
            { ref: '41:53', arabic: 'سَنُرِيهِمْ آيَاتِنَا فِي الْآفَاقِ وَفِي أَنفُسِهِمْ', translation: 'We will show them Our signs in the horizons and within themselves.' }
          ]
        },
        amanah: {
          statement: 'Support Lents brownfield remediation and community soil testing. Connect to environmental justice and soil remediation resources.',
          namesActive: [
            { arabic: 'الْحَق', transliteration: 'Al-Haqq', meaning: 'Soil truth as Amanah' },
            { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'Remediation as life-giving' }
          ],
          reshatReference: 'The Caretaker who exposes soil truth wears a thread of Taqwah.'
        },
        resourceCategories: ['Health', 'Justice', 'Community']
      }
    ]
  },
  // ── CINCINNATI — WALNUT HILLS ─────────────────────────────────────────────
  {
    wardId: 'walnut-hills',
    wardName: 'Walnut Hills',
    cityId: 'cincinnati',
    catalogCount: 23,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'peebles-corner-void',
        name: 'Peebles Corner — Civic Desert',
        arabicName: 'صَحْرَاءُ الْمَدَنِيَّة',
        category: 'community',
        wardSpecific: ['walnut-hills'],
        zahir: {
          sensory: 'Peebles Corner — institutions withdrew, leaving civic void. Absence documented as evidence.',
          sight: 'Empty storefronts, vacant lots.',
          smell: 'The smell of absence, vacant spaces.',
          texture: 'Empty storefronts, vacant lots.',
          sound: 'Silence, the sounds of absence.',
          huduthDhati: 'A civic desert as institutional withdrawal — its Huduth Dhati is both the physical (the empty storefronts, the vacant lots) and the social (the absence of institutions).',
          huduthZamani: 'Peebles Corner was once the heart of Walnut Hills. Its decline is a Huduth Zamani of institutional withdrawal from Black communities.'
        },
        sunnatProcesses: [
          {
            processName: 'Civic Desert',
            namesOfAllah: [
              { arabic: 'الشَّهِيد', transliteration: 'Al-Shahid', meaning: 'The Witness — absence documented as evidence' },
              { arabic: 'الْحَق', transliteration: 'Al-Haqq', meaning: 'The Truth — civic desert as truth of disinvestment' }
            ],
            description: 'The civic desert is Al-Shahid manifest — absence documented as evidence of institutional withdrawal.',
          }
        ],
        haqq: {
          zahirStatement: 'Peebles Corner civic desert in Walnut Hills, Cincinnati.',
          batinStatement: 'Al-Shahid manifest in absence documented as evidence — and Al-Haqq expressed in the truth of institutional withdrawal.',
          ayat: [
            { ref: '6:38', arabic: 'مَّا فَرَّطْنَا فِي الْكِتَابِ مِن شَيْءٍ', translation: 'We have not neglected in the Register a thing.' }
          ]
        },
        amanah: {
          statement: 'Support Walnut Hills Redevelopment Foundation. Connect to community development and civic infrastructure resources.',
          namesActive: [
            { arabic: 'الشَّهِيد', transliteration: 'Al-Shahid', meaning: 'Civic desert witness as Amanah' },
            { arabic: 'الْحَق', transliteration: 'Al-Haqq', meaning: 'Vacancy testimony as truth' }
          ],
          reshatReference: 'The Caretaker who documents the civic desert wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Justice', 'Income']
      }
    ]
  },
  // ── CINCINNATI — PRICE HILL ───────────────────────────────────────────────
  {
    wardId: 'price-hill',
    wardName: 'Price Hill',
    cityId: 'cincinnati',
    catalogCount: 20,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'price-hill-homeowner-persistence',
        name: 'Price Hill Homeowners — Appalachian Ecology',
        arabicName: 'صَبْرُ أَصْحَابِ الْمَنَازِل فِي بْرَايْس هِيل',
        category: 'community',
        wardSpecific: ['price-hill'],
        zahir: {
          sensory: 'Homeowners maintaining through decades of disinvestment and Appalachian poverty — stubborn maintenance.',
          sight: 'Maintained homes, the signs of homeowner persistence through disinvestment.',
          smell: 'Paint, wood, the smell of a maintained home.',
          texture: 'Maintained surfaces.',
          sound: 'Community voices, maintenance work.',
          huduthDhati: 'A homeowner community as persistence — its Huduth Dhati is both the physical (the homes, the repairs) and the social (the community that maintains through disinvestment).',
          huduthZamani: 'Price Hill has faced decades of disinvestment and Appalachian poverty.'
        },
        sunnatProcesses: [
          {
            processName: 'Homeowner Persistence',
            namesOfAllah: [
              { arabic: 'الصَّابِر', transliteration: 'As-Sabur', meaning: 'The Patient — homeowner persistence through disinvestment' },
              { arabic: 'الْبَاقِي', transliteration: 'Al-Baqi', meaning: 'The Everlasting — community persistence through decades of disinvestment' }
            ],
            description: 'Homeowner maintenance through disinvestment is As-Sabur manifest — patient persistence in the face of structural neglect.',
          }
        ],
        haqq: {
          zahirStatement: 'Price Hill homeowners maintaining through disinvestment and Appalachian poverty.',
          batinStatement: 'As-Sabur manifest in homeowner persistence — and Al-Baqi expressed in community enduring through decades of disinvestment.',
          ayat: [
            { ref: '30:60', arabic: 'فَاصْبِرْ إِنَّ وَعْدَ اللَّهِ حَقٌّ', translation: 'So be patient. Indeed, the promise of Allah is truth.' }
          ]
        },
        amanah: {
          statement: 'Support Price Hill homeowners and community development. Connect to housing justice and Appalachian community resources.',
          namesActive: [
            { arabic: 'الصَّابِر', transliteration: 'As-Sabur', meaning: 'Homeowner persistence as Amanah' },
            { arabic: 'الْبَاقِي', transliteration: 'Al-Baqi', meaning: 'Community endurance as everlasting' }
          ],
          reshatReference: 'The Caretaker who maintains their home through poverty wears a thread of Taqwah.'
        },
        resourceCategories: ['Housing', 'Community', 'Justice']
      }
    ]
  },
  // ── CINCINNATI — BOND HILL ────────────────────────────────────────────────
  {
    wardId: 'bond-hill',
    wardName: 'Bond Hill',
    cityId: 'cincinnati',
    catalogCount: 17,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'bond-hill-masjid-gathering',
        name: 'Masjid-ul-Quran — Friday Ecology',
        arabicName: 'مَسْجِدُ الْقُرْآن',
        category: 'community',
        wardSpecific: ['bond-hill'],
        zahir: {
          sensory: 'Masjid-ul-Quran gathering Cincinnati\'s African American Muslim community — Friday gathering weaves community.',
          sight: 'The masjid, the Friday congregation.',
          smell: 'The smell of a Muslim community gathering for Friday prayer.',
          texture: 'The texture of a community woven together through Friday prayer.',
          sound: 'Adhan, Quran, community voices.',
          huduthDhati: 'A masjid as community gathering — its Huduth Dhati is both the physical (the structure) and the spiritual (the community it weaves).',
          huduthZamani: 'Masjid-ul-Quran has served Cincinnati\'s African American Muslim community for decades.'
        },
        sunnatProcesses: [
          {
            processName: 'Masjid Gathering',
            namesOfAllah: [
              { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'The Gatherer — Friday prayer as community gathering' },
              { arabic: 'الْبَاقِي', transliteration: 'Al-Baqi', meaning: 'The Everlasting — masjid persisting through community change' }
            ],
            description: 'The Friday gathering is Al-Jami manifest — the Gatherer weaving community through prayer.',
          }
        ],
        haqq: {
          zahirStatement: 'Masjid-ul-Quran Friday gathering in Bond Hill, Cincinnati.',
          batinStatement: 'Al-Jami manifest in Friday gathering — and Al-Baqi expressed in the masjid persisting through community change.',
          ayat: [
            { ref: '62:9', arabic: 'يَا أَيُّهَا الَّذِينَ آمَنُوا إِذَا نُودِيَ لِلصَّلَاةِ مِن يَوْمِ الْجُمُعَةِ فَاسْعَوْا إِلَىٰ ذِكْرِ اللَّهِ', translation: 'O you who believe, when the call to prayer is made on Friday, hasten to the remembrance of Allah.' }
          ]
        },
        amanah: {
          statement: 'Support Masjid-ul-Quran and Cincinnati\'s African American Muslim community. Connect to Islamic community development resources.',
          namesActive: [
            { arabic: 'الْجَامِع', transliteration: 'Al-Jami', meaning: 'Friday gathering as Amanah' },
            { arabic: 'الْبَاقِي', transliteration: 'Al-Baqi', meaning: 'Masjid persistence as everlasting' }
          ],
          reshatReference: 'The Caretaker who attends the Friday gathering wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Education', 'Health']
      }
    ]
  },
  // ── CINCINNATI — NORTHSIDE ────────────────────────────────────────────────
  {
    wardId: 'northside',
    wardName: 'Northside',
    cityId: 'cincinnati',
    catalogCount: 15,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'northside-arts-equity',
        name: 'Northside Arts Community — Creative Displacement',
        arabicName: 'عَدَالَةُ الْفُنُون',
        category: 'community',
        wardSpecific: ['northside'],
        zahir: {
          sensory: 'Art community grappling with gentrification it inadvertently accelerated — creative economy as displacement vector.',
          sight: 'Art galleries, gentrified storefronts.',
          smell: 'Art spaces, the smell of a neighborhood in gentrification.',
          texture: 'Art materials, gentrified spaces.',
          sound: 'Art community voices.',
          huduthDhati: 'An arts community as displacement vector — its Huduth Dhati is both the physical (the galleries, the art spaces) and the social (the displacement it inadvertently caused).',
          huduthZamani: 'Northside\'s arts community has grappled with its role in gentrification for years.'
        },
        sunnatProcesses: [
          {
            processName: 'Arts Equity',
            namesOfAllah: [
              { arabic: 'الْعَدْل', transliteration: 'Al-Adl', meaning: 'The Just — arts equity as justice question' },
              { arabic: 'الْخَالِق', transliteration: 'Al-Khaliq', meaning: 'The Creator — creative economy as creation' }
            ],
            description: 'Arts equity is Al-Adl\'s question — who benefits from the creative economy, and who is displaced by it?',
          }
        ],
        haqq: {
          zahirStatement: 'Northside arts community grappling with creative displacement in Cincinnati.',
          batinStatement: 'Al-Adl manifest in the justice question of creative displacement — and Al-Khaliq expressed in the ethics of creative economy.',
          ayat: [
            { ref: '16:90', arabic: 'إِنَّ اللَّهَ يَأْمُرُ بِالْعَدْلِ وَالْإِحْسَانِ', translation: 'Indeed, Allah commands justice and good conduct.' }
          ]
        },
        amanah: {
          statement: 'Support arts equity in Northside. Connect to creative economy justice and anti-displacement resources.',
          namesActive: [
            { arabic: 'الْعَدْل', transliteration: 'Al-Adl', meaning: 'Arts equity as justice' },
            { arabic: 'الْخَالِق', transliteration: 'Al-Khaliq', meaning: 'Creative economy as creation ethics' }
          ],
          reshatReference: 'The Caretaker who practices design justice wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Justice', 'Income']
      }
    ]
  },
  // ── OMAHA — SOUTH OMAHA ───────────────────────────────────────────────────
  {
    wardId: 'south-omaha',
    wardName: 'South Omaha',
    cityId: 'omaha',
    catalogCount: 28,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'south-omaha-meatpacking',
        name: 'South Omaha Meatpacking — Worker Ecology',
        arabicName: 'بِيئَةُ الْعُمَّال فِي أُومَاهَا',
        category: 'community',
        wardSpecific: ['south-omaha'],
        zahir: {
          sensory: 'Meatpacking workers — immigrant labor sustaining industrial food production. Pay stub as justice language.',
          sight: 'Meatpacking plants, worker communities.',
          smell: 'Industrial food production.',
          texture: 'Industrial work, community spaces.',
          sound: 'Worker voices, industrial sounds.',
          huduthDhati: 'A meatpacking community as worker ecology — its Huduth Dhati is both the physical (the plants, the work) and the social (the immigrant labor that sustains it).',
          huduthZamani: 'South Omaha\'s meatpacking industry has relied on immigrant labor for over a century.'
        },
        sunnatProcesses: [
          {
            processName: 'Worker Ecology',
            namesOfAllah: [
              { arabic: 'الْعَدْل', transliteration: 'Al-Adl', meaning: 'The Just — worker justice as divine justice' },
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — worker provision as Rizq' }
            ],
            description: 'Worker ecology is Al-Adl\'s question — the workers who feed America deserve justice in return.',
          }
        ],
        haqq: {
          zahirStatement: 'South Omaha meatpacking worker community.',
          batinStatement: 'Al-Adl manifest in worker justice — and Ar-Razzaq expressed in immigrant labor as provision.',
          ayat: [
            { ref: '16:90', arabic: 'إِنَّ اللَّهَ يَأْمُرُ بِالْعَدْلِ وَالْإِحْسَانِ', translation: 'Indeed, Allah commands justice and good conduct.' }
          ]
        },
        amanah: {
          statement: 'Support South Omaha meatpacking workers. Connect to labor justice and immigrant worker resources.',
          namesActive: [
            { arabic: 'الْعَدْل', transliteration: 'Al-Adl', meaning: 'Worker justice as Amanah' },
            { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'Immigrant provision as Rizq' }
          ],
          reshatReference: 'The Caretaker who advocates for worker justice wears a thread of Taqwah.'
        },
        resourceCategories: ['Income', 'Justice', 'Community']
      }
    ]
  },
  // ── OMAHA — NORTH OMAHA ───────────────────────────────────────────────────
  {
    wardId: 'north-omaha',
    wardName: 'North Omaha',
    cityId: 'omaha',
    catalogCount: 24,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'north-omaha-24th-lake',
        name: '24th and Lake — Intersection Memory',
        arabicName: 'ذَاكِرَةُ التَّقَاطُع',
        category: 'community',
        wardSpecific: ['north-omaha'],
        zahir: {
          sensory: '24th and Lake — once the hub of Black Omaha. Crossroads revealing multiple histories simultaneously.',
          sight: 'The intersection, community spaces.',
          smell: 'The smell of a community preserving its history.',
          texture: 'Community spaces.',
          sound: 'Community voices.',
          huduthDhati: 'An intersection as community memory — its Huduth Dhati is both the physical (the corner, the buildings) and the historical (the multiple histories it reveals).',
          huduthZamani: '24th and Lake was the hub of Black Omaha for decades.'
        },
        sunnatProcesses: [
          {
            processName: 'Intersection Memory',
            namesOfAllah: [
              { arabic: 'الْبَصِير', transliteration: 'Al-Basir', meaning: 'The All-Seeing — intersection as historical witness' },
              { arabic: 'الشَّهِيد', transliteration: 'Al-Shahid', meaning: 'The Witness — crossroads revealing multiple histories' }
            ],
            description: 'The intersection as community memory is Al-Basir manifest — seeing multiple histories simultaneously.',
          }
        ],
        haqq: {
          zahirStatement: '24th and Lake intersection in North Omaha.',
          batinStatement: 'Al-Basir manifest in intersection memory — and Al-Shahid expressed in historical testimony at the crossroads.',
          ayat: [
            { ref: '6:38', arabic: 'مَّا فَرَّطْنَا فِي الْكِتَابِ مِن شَيْءٍ', translation: 'We have not neglected in the Register a thing.' }
          ]
        },
        amanah: {
          statement: 'Support North Omaha community memory preservation. Connect to cultural heritage and community development resources.',
          namesActive: [
            { arabic: 'الْبَصِير', transliteration: 'Al-Basir', meaning: 'Intersection memory as seeing' },
            { arabic: 'الشَّهِيد', transliteration: 'Al-Shahid', meaning: 'Historical testimony as witness' }
          ],
          reshatReference: 'The Caretaker who preserves intersection memory wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Education', 'Justice']
      }
    ]
  },
  // ── OMAHA — BENSON ────────────────────────────────────────────────────────
  {
    wardId: 'benson',
    wardName: 'Benson',
    cityId: 'omaha',
    catalogCount: 19,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'benson-commercial-revival',
        name: 'Benson Commercial Revival — Small Business Ecology',
        arabicName: 'إِحْيَاءُ التِّجَارَة فِي بِينْسُون',
        category: 'community',
        wardSpecific: ['benson'],
        zahir: {
          sensory: 'Commercial corridor revival — local restaurants and food entrepreneurs bringing life to vacant storefronts.',
          sight: 'Revived storefronts, local restaurants, food entrepreneurs.',
          smell: 'Local food, the smell of a reviving commercial corridor.',
          texture: 'Revived storefronts, local food.',
          sound: 'Community voices, local businesses.',
          huduthDhati: 'A commercial revival as community life — its Huduth Dhati is both the physical (the storefronts, the businesses) and the social (the community life it creates).',
          huduthZamani: 'Benson\'s commercial corridor was vacant for years. Its revival is a Huduth Zamani of Al-Muhyi in commercial life.'
        },
        sunnatProcesses: [
          {
            processName: 'Small Business Ecology',
            namesOfAllah: [
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — small business as provision ecology' },
              { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'The Life-Giver — commercial revival as life restoration' }
            ],
            description: 'Small business ecology is Ar-Razzaq manifest — provision flowing through local entrepreneurship.',
          }
        ],
        haqq: {
          zahirStatement: 'Benson commercial revival in Omaha.',
          batinStatement: 'Al-Muhyi manifest in commercial revival — and Ar-Razzaq expressed in small business ecology.',
          ayat: [
            { ref: '22:5', arabic: 'وَتَرَى الْأَرْضَ هَامِدَةً فَإِذَا أَنزَلْنَا عَلَيْهَا الْمَاءَ اهْتَزَّتْ وَرَبَتْ', translation: 'You see the earth barren, but when We send down rain, it quivers and grows.' }
          ]
        },
        amanah: {
          statement: 'Support Benson small businesses and commercial revival. Connect to small business development and food entrepreneurship resources.',
          namesActive: [
            { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'Small business provision as Amanah' },
            { arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', meaning: 'Commercial revival as life-giving' }
          ],
          reshatReference: 'The Caretaker who supports small business revival wears a thread of Taqwah.'
        },
        resourceCategories: ['Income', 'Community', 'Food']
      }
    ]
  },
  // ── OMAHA — MIDTOWN ───────────────────────────────────────────────────────
  {
    wardId: 'midtown',
    wardName: 'Midtown',
    cityId: 'omaha',
    catalogCount: 16,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'gifford-park-affordable-housing',
        name: 'Gifford Park — Affordable Housing Ecology',
        arabicName: 'بِيئَةُ الإِسْكَان الْمِيسُور',
        category: 'community',
        wardSpecific: ['midtown'],
        zahir: {
          sensory: 'Neighborhood association maintaining affordable housing through organized resistance to speculation — decades-long effort.',
          sight: 'Affordable housing, neighborhood association.',
          smell: 'The smell of a community working to stay affordable.',
          texture: 'Community housing, neighborhood spaces.',
          sound: 'Community voices, organizing.',
          huduthDhati: 'A neighborhood association as affordable housing ecology — its Huduth Dhati is both the physical (the housing, the properties) and the social (the organized resistance to speculation).',
          huduthZamani: 'Gifford Park has maintained affordable housing for decades through organized resistance.'
        },
        sunnatProcesses: [
          {
            processName: 'Affordable Housing Ecology',
            namesOfAllah: [
              { arabic: 'الصَّابِر', transliteration: 'As-Sabur', meaning: 'The Patient — decades-long affordable housing advocacy' },
              { arabic: 'الْبَاقِي', transliteration: 'Al-Baqi', meaning: 'The Everlasting — affordable housing as community permanence' }
            ],
            description: 'Affordable housing advocacy is As-Sabur manifest — patient work that maintains community permanence.',
          }
        ],
        haqq: {
          zahirStatement: 'Gifford Park affordable housing ecology in Omaha.',
          batinStatement: 'As-Sabur manifest in decades-long advocacy — and Al-Baqi expressed in affordable housing as community permanence.',
          ayat: [
            { ref: '30:60', arabic: 'فَاصْبِرْ إِنَّ وَعْدَ اللَّهِ حَقٌّ', translation: 'So be patient. Indeed, the promise of Allah is truth.' }
          ]
        },
        amanah: {
          statement: 'Support Gifford Park affordable housing advocacy. Connect to housing justice and anti-speculation resources.',
          namesActive: [
            { arabic: 'الصَّابِر', transliteration: 'As-Sabur', meaning: 'Affordable housing advocacy as Amanah' },
            { arabic: 'الْبَاقِي', transliteration: 'Al-Baqi', meaning: 'Community permanence as everlasting' }
          ],
          reshatReference: 'The Caretaker who maintains affordable housing wears a thread of Taqwah.'
        },
        resourceCategories: ['Housing', 'Community', 'Justice']
      }
    ]
  },
  // ── OMAHA — FLORENCE ──────────────────────────────────────────────────────
  {
    wardId: 'florence',
    wardName: 'Florence',
    cityId: 'omaha',
    catalogCount: 13,
    examples: [
      ...BASE_EXAMPLES,
      {
        id: 'florence-missouri-river',
        name: 'Florence Missouri River Access',
        arabicName: 'وُصُولٌ إِلَى نَهْر مِيسُورِي',
        category: 'water',
        wardSpecific: ['florence'],
        zahir: {
          sensory: 'Florence — a historic neighborhood on the Missouri River, with access to the waterfront and the history of Mormon settlement.',
          sight: 'The Missouri River, the historic neighborhood, the waterfront.',
          smell: 'River air, the smell of a waterfront community.',
          texture: 'The texture of a historic waterfront community.',
          sound: 'The river, birds, community voices.',
          huduthDhati: 'A historic waterfront community — its Huduth Dhati is shaped by proximity to the Missouri River.',
          huduthZamani: 'Florence was settled in the 1840s as a Mormon staging ground. Its history is a Huduth Zamani of migration and settlement.'
        },
        sunnatProcesses: [
          {
            processName: 'River as Historical Witness',
            namesOfAllah: [
              { arabic: 'الشَّهِيد', transliteration: 'Al-Shahid', meaning: 'The Witness — the river as historical witness' },
              { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'The Provider — river as provision' }
            ],
            description: 'The Missouri River has witnessed centuries of human history — a Sunnat of Al-Shahid manifest in the river as historical witness.',
          }
        ],
        haqq: {
          zahirStatement: 'Florence on the Missouri River in Omaha.',
          batinStatement: 'Al-Shahid manifest in the river as historical witness — and Ar-Razzaq expressed in river provision.',
          ayat: [
            { ref: '16:14', arabic: 'وَهُوَ الَّذِي سَخَّرَ الْبَحْرَ لِتَأْكُلُوا مِنْهُ لَحْمًا طَرِيًّا', translation: 'And it is He who subjected the sea for you to eat from it tender meat.' }
          ]
        },
        amanah: {
          statement: 'Support Florence community organizations. Connect to environmental justice and community development resources.',
          namesActive: [
            { arabic: 'الشَّهِيد', transliteration: 'Al-Shahid', meaning: 'Historical witness as Amanah' },
            { arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', meaning: 'River provision' }
          ],
          reshatReference: 'The Caretaker who honors Florence\'s history wears a thread of Taqwah.'
        },
        resourceCategories: ['Community', 'Justice', 'Health']
      }
    ]
  }
];

// ─── FISSION CHAIN DATA ──────────────────────────────────────────────────────

export const FISSION_CHAIN = [
  { id: 'observation', label: 'OBSERVATION', arabic: 'مُشَاهَدَة', x: 50, y: 10 },
  { id: 'huduth-dhati', label: 'HUDUTH DHATI', arabic: 'حُدُوث ذَاتِيّ', x: 20, y: 35 },
  { id: 'huduth-zamani', label: 'HUDUTH ZAMANI', arabic: 'حُدُوث زَمَانِيّ', x: 80, y: 35 },
  { id: 'wujub', label: 'WUJUB AL-WUJUD', arabic: 'وُجُوب الْوُجُود', x: 50, y: 60 },
  { id: 'al-wajib', label: 'AL-WAJIB', arabic: 'الْوَاجِب', x: 50, y: 80 },
  { id: 'allah', label: 'ALLAH', arabic: 'اللَّه', x: 50, y: 95 },
];

export const FISSION_CONNECTIONS = [
  { from: 'observation', to: 'huduth-dhati', label: 'What is it?' },
  { from: 'observation', to: 'huduth-zamani', label: 'When was it?' },
  { from: 'huduth-dhati', to: 'wujub', label: 'Contingent existence' },
  { from: 'huduth-zamani', to: 'wujub', label: 'Temporal contingency' },
  { from: 'wujub', to: 'al-wajib', label: 'Requires necessary being' },
  { from: 'al-wajib', to: 'allah', label: 'The Necessary Being' },
];

// ─── COLLECTIVE CATALOG ───────────────────────────────────────────────────────

export const COLLECTIVE_CATALOG = [
  { id: 'cc1', title: 'Fruit Tree on Auburn Ave', city: 'Atlanta', ward: 'West End', name: 'Ar-Razzaq', description: 'A fig tree growing in a vacant lot, providing fruit to the neighborhood.', timestamp: '2024-03-15' },
  { id: 'cc2', title: 'Prairie Grass in Brightmoor', city: 'Detroit', ward: 'Brightmoor', name: 'Al-Muhyi', description: 'Big Bluestem returning to a vacant lot — 6 feet tall, roots going deep.', timestamp: '2024-03-14' },
  { id: 'cc3', title: 'Halal Market on Conant', city: 'Detroit', ward: 'Banglatown', name: 'Al-Jami', description: 'Bangladeshi and Yemeni grocers side by side — provision corridor.', timestamp: '2024-03-13' },
  { id: 'cc4', title: 'Lake Michigan at South Shore', city: 'Chicago', ward: 'South Shore', name: 'Ar-Razzaq', description: 'The great lake visible from the neighborhood — 21% of world\'s fresh water.', timestamp: '2024-03-12' },
  { id: 'cc5', title: 'BeltLine Trail in West End', city: 'Atlanta', ward: 'West End', name: 'Al-Muhyi', description: 'Former rail corridor transformed into community trail.', timestamp: '2024-03-11' },
  { id: 'cc6', title: 'Masjid in Hill District', city: 'Pittsburgh', ward: 'Hill District', name: 'Al-Baqi', description: 'Masjid persisting through urban renewal — Al-Baqi manifest.', timestamp: '2024-03-10' },
  { id: 'cc7', title: 'Hayti Heritage Center', city: 'Durham', ward: 'Hayti', name: 'Al-Shahid', description: 'Black Wall Street memory preserved — Al-Shahid in community memory.', timestamp: '2024-03-09' },
  { id: 'cc8', title: '9th Street Divide', city: 'Louisville', ward: 'West Louisville', name: 'Al-Adl', description: 'Infrastructure segregation encoding 20-year life expectancy gap.', timestamp: '2024-03-08' },
];

// ─── CITY IRL SUMMARY ─────────────────────────────────────────────────────────

export const CITY_IRL_SUMMARY = [
  { cityId: 'atlanta', cityName: 'Atlanta', totalCataloged: 156, wardsActive: 8, topAyah: 'Fruit Tree', topName: 'Ar-Razzaq' },
  { cityId: 'chicago', cityName: 'Chicago', totalCataloged: 203, wardsActive: 8, topAyah: 'Lake Michigan', topName: 'Al-Wasi' },
  { cityId: 'cincinnati', cityName: 'Cincinnati', totalCataloged: 89, wardsActive: 8, topAyah: 'Ohio River', topName: 'Al-Muhyi' },
  { cityId: 'detroit', cityName: 'Detroit', totalCataloged: 112, wardsActive: 8, topAyah: 'Prairie Succession', topName: 'Al-Muhyi' },
  { cityId: 'pittsburgh', cityName: 'Pittsburgh', totalCataloged: 94, wardsActive: 8, topAyah: 'River Confluence', topName: 'Al-Jami' },
  { cityId: 'durham', cityName: 'Durham', totalCataloged: 78, wardsActive: 8, topAyah: 'Hayti Heritage', topName: 'Al-Shahid' },
  { cityId: 'louisville', cityName: 'Louisville', totalCataloged: 87, wardsActive: 8, topAyah: '9th Street Divide', topName: 'Al-Adl' },
  { cityId: 'portland', cityName: 'Portland', totalCataloged: 91, wardsActive: 8, topAyah: 'Alberta Displacement', topName: 'Al-Shahid' },
  { cityId: 'omaha', cityName: 'Omaha', totalCataloged: 67, wardsActive: 8, topAyah: '24th and Lake', topName: 'Al-Shahid' },
];
